// src/pages/ordens-servico/AtendimentoForm.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import { NIVEIS } from "../../config/niveis";
import { nivelUsuario } from "../../utils/permissoes";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";

import { db } from "../../config/firebase";
import { STATUS_ATENDIMENTO, CORES_STATUS, isStatusBloqueado } from "../../config/status";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import { apiGetAiConfig, apiChatAi } from "../../services/aiApi";
import { uploadToStorage } from "../../utils/storageUpload";
import { marcarPropostasFinalizadasPorSessao } from "../../services/propostasStatusService";

// Gera próximo subnúmero de atendimento DENTRO da OS
async function obterProximoSubnumeroAtendimento(osId, { grupoId, empresaId }) {
  const contadorRef = doc(db, "contadores", `atendimentos_${osId}`);

  const proximo = await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(contadorRef);

    let atual = 0;
    if (snap.exists() && snap.data().ultimoSubnumero != null) {
      atual = snap.data().ultimoSubnumero;
    }

    const novoValor = atual + 1;

    transaction.set(
      contadorRef,
      {
        grupoId,
        empresaId: empresaId ?? null,
        ultimoSubnumero: novoValor,
      },
      { merge: true }
    );

    return novoValor;
  });

  return proximo;
}

// Detecta se um contrato é do tipo "Avulso" (sem contrato, requer proposta)
function isContratoAvulso(contratoTipo) {
  return String(contratoTipo || "").toLowerCase().includes("avulso");
}


function AtendimentoForm() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { osId } = useParams();
  const isMobile = useIsMobile(900);

  // Determinar para onde voltar
  const voltarPara = location?.state?.voltarPara || null;
  const navegarVoltar = () => navigate(voltarPara ? voltarPara : -1);

  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [os, setOs] = useState(null);
  const [carregandoOs, setCarregandoOs] = useState(true);
  const [osResumoAberto, setOsResumoAberto] = useState(false);

  const [historicoOs, setHistoricoOs] = useState([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);

  const [procedimentosRelacionado, setProcedimentosRelacionado] = useState([]);
  const [carregandoProcedimentos, setCarregandoProcedimentos] = useState(false);

  // Validação de proposta para contratos avulsos
  const [osContratoAvulso, setOsContratoAvulso] = useState(false);
  const [propostaAprovada, setPropostaAprovada] = useState(false);

  const [etapa, setEtapa] = useState("PRE"); // PRE | EM_ANDAMENTO | FINALIZACAO
  const [atendimentoId, setAtendimentoId] = useState(null);
  
  // Controle de acompanhamento
  const [tecnicoPrincipalId, setTecnicoPrincipalId] = useState(null);
  const [tecnicoPrincipalNome, setTecnicoPrincipalNome] = useState("");
  const [acompanhantes, setAcompanhantes] = useState([]);
  const [tecnicosDisponiveis, setTecnicosDisponiveis] = useState([]);
  const [novoAcompanhanteId, setNovoAcompanhanteId] = useState("");
  const [modoAcompanhamento, setModoAcompanhamento] = useState(false); // true se o usuário atual é acompanhante

  const [defeitoApresentado, setDefeitoApresentado] = useState("");
  const [defeitoEncontrado, setDefeitoEncontrado] = useState("");
  const [servicoExecutado, setServicoExecutado] = useState("");
  const [maquinaContinuaParada, setMaquinaContinuaParada] = useState("Não");
  const [statusAtendimento, setStatusAtendimento] = useState("");

  // Materiais usados (Estoque Interno)
  const [estoqueItens, setEstoqueItens] = useState([]);
  const [carregandoEstoque, setCarregandoEstoque] = useState(false);
  const [estoqueSelecionadoId, setEstoqueSelecionadoId] = useState("");
  const [estoqueQtd, setEstoqueQtd] = useState("1");
  const [materiaisUsados, setMateriaisUsados] = useState([]); // [{estoqueId, produto, codigo, local, quantidade}]
  const [materiaisErro, setMateriaisErro] = useState("");

  // Fotos da finalização
  const [fotosFinalizacao, setFotosFinalizacao] = useState([]); // Arquivos selecionados
  const [fotosFinalizacaoUrls, setFotosFinalizacaoUrls] = useState([]); // URLs após upload
  const [uploadandoFotos, setUploadandoFotos] = useState(false);

  // Checklist (Instalação) — quando a OS tiver checklistModeloId/checklistItens
  const [checklistRespostas, setChecklistRespostas] = useState([]); // [{ valor }]
  const [checklistObservacoes, setChecklistObservacoes] = useState("");

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [salvando, setSalvando] = useState(false);

  // IA (primeira integração: chat simples com contexto da OS)
  const [aiCfg, setAiCfg] = useState(null); // {enabled, hasKey, modelDefault}
  const [aiChat, setAiChat] = useState([]); // [{role:'user'|'assistant', content:string, ts:number}]
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  const normalizarTexto = (txt) =>
    String(txt || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const normalizarTipoResposta = (tipo) =>
    normalizarTexto(tipo).replace(/\s+/g, "_").toUpperCase();

  const getChecklistInfo = (osData) => {
    const base = osData || os || {};
    const itens = Array.isArray(base?.checklistItens) ? base.checklistItens : [];
    const ativo = !!(base?.checklistModeloId && itens.length > 0);
    const tipoServicoTxt = normalizarTexto(base?.tipoServico || "");
    const ehInstalacao = tipoServicoTxt.includes("instal");
    return { itens, ativo, ehInstalacao };
  };

  function normalizarListaAcompanhantes(lista) {
    const mapa = new Map();
    (Array.isArray(lista) ? lista : []).forEach((item) => {
      const id = String(item?.id || "").trim();
      if (!id) return;
      if (id === String(tecnicoPrincipalId || "").trim()) return;
      if (!mapa.has(id)) {
        mapa.set(id, {
          id,
          nome: String(item?.nome || "").trim() || "Técnico",
          entradaEm: item?.entradaEm || new Date().toISOString(),
        });
      }
    });
    return Array.from(mapa.values());
  }

  async function sincronizarAcompanhantesNasSessoes(listaAcompanhantes) {
    if (!usuarioLogado?.grupoId || !os?.id) return;

    try {
      const snapItens = await getDocs(
        query(
          collection(db, "atendimentos_sessao_itens"),
          where("grupoId", "==", usuarioLogado.grupoId),
          where("osId", "==", os.id)
        )
      );

      const sessaoIds = Array.from(
        new Set(snapItens.docs.map((itemDoc) => String(itemDoc.data()?.sessaoId || "")).filter(Boolean))
      );
      if (!sessaoIds.length) return;

      const acompanhantesNormalizados = normalizarListaAcompanhantes(listaAcompanhantes);
      const acompanhantesIds = acompanhantesNormalizados.map((item) => item.id);
      const acompanhantesNomes = acompanhantesNormalizados.map((item) => item.nome);

      await Promise.all(
        sessaoIds.map((sessaoId) =>
          updateDoc(doc(db, "atendimentos_sessoes", sessaoId), {
            tecnicosAcompanhantesIds: acompanhantesIds,
            tecnicosAcompanhantesNomes: acompanhantesNomes,
            updatedAt: serverTimestamp(),
          })
        )
      );
    } catch (eSync) {
      console.warn("Falha ao sincronizar acompanhantes nas sessões:", eSync);
    }
  }

  async function obterAcompanhantesPlanejamentoDaOs() {
    if (!usuarioLogado?.grupoId || !os?.id) return [];

    try {
      const snapItens = await getDocs(
        query(
          collection(db, "atendimentos_sessao_itens"),
          where("grupoId", "==", usuarioLogado.grupoId),
          where("osId", "==", os.id)
        )
      );

      const sessaoIds = Array.from(
        new Set(snapItens.docs.map((itemDoc) => String(itemDoc.data()?.sessaoId || "")).filter(Boolean))
      );
      if (!sessaoIds.length) return [];

      const nomesPorId = {};
      const idsSet = new Set();

      await Promise.all(
        sessaoIds.map(async (sessaoId) => {
          try {
            const sessaoSnap = await getDoc(doc(db, "atendimentos_sessoes", sessaoId));
            if (!sessaoSnap.exists()) return;
            const dadosSessao = sessaoSnap.data() || {};
            const ids = Array.isArray(dadosSessao.tecnicosAcompanhantesIds)
              ? dadosSessao.tecnicosAcompanhantesIds.map((id) => String(id || "").trim()).filter(Boolean)
              : [];
            const nomes = Array.isArray(dadosSessao.tecnicosAcompanhantesNomes)
              ? dadosSessao.tecnicosAcompanhantesNomes
              : [];

            ids.forEach((id, idx) => {
              if (!id || id === String(usuarioLogado.uid || "")) return;
              idsSet.add(id);
              if (!nomesPorId[id] && nomes[idx]) {
                nomesPorId[id] = String(nomes[idx] || "").trim();
              }
            });
          } catch {
            // ignora sessão individual inválida
          }
        })
      );

      if (!idsSet.size) return [];

      const tecnicosPorId = {};
      (tecnicosDisponiveis || []).forEach((tecnico) => {
        tecnicosPorId[String(tecnico?.id || "")] = tecnico;
      });

      return Array.from(idsSet).map((id) => ({
        id,
        nome:
          nomesPorId[id] ||
          tecnicosPorId[id]?.nome ||
          tecnicosPorId[id]?.displayName ||
          tecnicosPorId[id]?.email ||
          "Técnico",
        entradaEm: new Date().toISOString(),
      }));
    } catch (ePlan) {
      console.warn("Falha ao obter acompanhantes do planejamento:", ePlan);
      return [];
    }
  }

  async function gerarRelatorioSessao({ sessaoId }) {
    if (!sessaoId || !usuarioLogado?.grupoId) return;
    try {
      await fetch("/api/atendimentos/relatorio/gerar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          grupoId: usuarioLogado.grupoId,
          sessaoId,
        }),
      });
    } catch (e) {
      console.warn("Falha ao gerar relatorio da sessao:", e);
    }
  }

  async function finalizarSessoesSeConcluidas({ osAtualId, osAtualStatus }) {
    if (!usuarioLogado?.grupoId || !osAtualId) return;

    const itensSnap = await getDocs(
      query(
        collection(db, "atendimentos_sessao_itens"),
        where("grupoId", "==", usuarioLogado.grupoId),
        where("osId", "==", osAtualId)
      )
    );

    if (!itensSnap.docs.length) return;

    const sessaoIds = Array.from(
      new Set(itensSnap.docs.map((d) => String(d.data()?.sessaoId || "")).filter(Boolean))
    );

    const statusCache = new Map();
    if (osAtualStatus) statusCache.set(String(osAtualId), osAtualStatus);

    await Promise.all(
      sessaoIds.map(async (sessaoId) => {
        try {
          const sessaoItensSnap = await getDocs(
            query(
              collection(db, "atendimentos_sessao_itens"),
              where("grupoId", "==", usuarioLogado.grupoId),
              where("sessaoId", "==", sessaoId)
            )
          );

          const osIds = Array.from(
            new Set(
              sessaoItensSnap.docs
                .map((d) => d.data())
                .filter((it) => String(it?.tipo || "").toUpperCase() === "OS" || it?.osId)
                .map((it) => String(it?.osId || ""))
                .filter(Boolean)
            )
          );

          if (!osIds.length) return;

          const statusByOs = await Promise.all(
            osIds.map(async (id) => {
              if (statusCache.has(id)) return statusCache.get(id);
              const osDoc = await getDoc(doc(db, "ordensServico", id));
              const statusNome = osDoc.exists() ? (osDoc.data()?.statusNome || osDoc.data()?.status || "") : "";
              statusCache.set(id, statusNome);
              return statusNome;
            })
          );

          const todasFinalizadas = statusByOs.every((st) => isStatusBloqueado(st));
          if (!todasFinalizadas) return;

          const sessaoRef = doc(db, "atendimentos_sessoes", sessaoId);
          const sessaoSnap = await getDoc(sessaoRef);
          if (!sessaoSnap.exists()) return;
          const sessaoAtual = sessaoSnap.data();
          if (sessaoAtual?.status === "ATENDIDA") {
            await marcarPropostasFinalizadasPorSessao(sessaoId);
            return;
          }
          await updateDoc(sessaoRef, {
            status: "ATENDIDA",
            fimEm: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });

          await marcarPropostasFinalizadasPorSessao(sessaoId);

          await gerarRelatorioSessao({ sessaoId });
        } catch (e) {
          console.warn("Falha ao validar sessao para fechamento:", e);
        }
      })
    );
  }

  useEffect(() => {
    async function carregarOs() {
      if (!usuarioLogado?.grupoId || !osId) return;

      setCarregandoOs(true);
      setErro("");

      try {
        const ref = doc(db, "ordensServico", osId);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setErro("OS não encontrada.");
          setCarregandoOs(false);
          return;
        }

        const dados = { id: snap.id, ...snap.data() };

        if (dados.grupoId && dados.grupoId !== usuarioLogado.grupoId) {
          setErro("Esta OS pertence a outro grupo.");
          setCarregandoOs(false);
          return;
        }

        setOs(dados);

        const descr =
          dados.descricao ||
          dados.titulo ||
          dados.defeitoApresentado ||
          "";
        setDefeitoApresentado(descr);

        // Inicializar checklist (se houver)
        try {
          const { itens, ativo } = getChecklistInfo(dados);
          if (ativo) {
            setChecklistRespostas(itens.map(() => ({ valor: "" })));
            setChecklistObservacoes("");
          } else {
            setChecklistRespostas([]);
            setChecklistObservacoes("");
          }
        } catch {
          setChecklistRespostas([]);
          setChecklistObservacoes("");
        }

        // Carregar histórico de OS com o mesmo defeito
        try {
          setCarregandoHistorico(true);

          const ordensRef = collection(db, "ordensServico");
          let qHistorico = null;

          if (dados.defeitoId) {
            qHistorico = query(
              ordensRef,
              where("grupoId", "==", usuarioLogado.grupoId),
              where("defeitoId", "==", dados.defeitoId)
            );
          } else if (dados.defeitoNome) {
            qHistorico = query(
              ordensRef,
              where("grupoId", "==", usuarioLogado.grupoId),
              where("defeitoNome", "==", dados.defeitoNome)
            );
          }

          if (qHistorico) {
            const snapHist = await getDocs(qHistorico);
            const listaHist = snapHist.docs
              .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
              .filter((osItem) => osItem.id !== dados.id);
            setHistoricoOs(listaHist);
          } else {
            setHistoricoOs([]);
          }
        } catch (eHist) {
          console.error(
            "Erro ao carregar histórico de OS com mesmo defeito:",
            eHist
          );
          setHistoricoOs([]);
        } finally {
          setCarregandoHistorico(false);
        }

        // Carregar procedimentos vinculados à máquina
        try {
          if (dados.maquinaId) {
            setCarregandoProcedimentos(true);
            const procedimentosRef = collection(db, "procedimentos");
            const qProc = query(
              procedimentosRef,
              where("grupoId", "==", usuarioLogado.grupoId),
              where("maquinasIds", "array-contains", dados.maquinaId)
            );
            const snapProc = await getDocs(qProc);
            const listaProc = snapProc.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            }));
            setProcedimentosRelacionado(listaProc);
          } else {
            setProcedimentosRelacionado([]);
          }
        } catch (eProc) {
          console.error(
            "Erro ao carregar procedimentos relacionados à máquina:",
            eProc
          );
          setProcedimentosRelacionado([]);
        } finally {
          setCarregandoProcedimentos(false);
        }

        // ✅ Validar contrato avulso e proposta aprovada (fonte: Financeiro/Contrato)
        try {
          setOsContratoAvulso(false);
          setPropostaAprovada(false);

          const empresaIdRef = String(dados.empresaId || "").trim();
          const setorIdRef = String(dados.setorId || "").trim();

          let contratoAtivo = null;
          if (empresaIdRef) {
            const filtros = [
              where("grupoId", "==", usuarioLogado.grupoId),
              where("clienteId", "==", empresaIdRef),
            ];
            if (setorIdRef) filtros.push(where("academiasIds", "array-contains", setorIdRef));

            const snapContratos = await getDocs(
              query(collection(db, "financeiro_contratos"), ...filtros)
            );
            contratoAtivo = snapContratos.docs
              .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
              .find((c) => String(c?.status || "").toUpperCase() === "APROVADO") || null;
          }

          const contratoTipoFinanceiro = String(contratoAtivo?.contratoTipo || "").trim();
          if (!contratoAtivo || !contratoTipoFinanceiro || isContratoAvulso(contratoTipoFinanceiro)) {
            setOsContratoAvulso(true);

            // Se é avulso, verifica aprovação da proposta nas sessões que contenham esta OS
            const snapItensSessao = await getDocs(
              query(
                collection(db, "atendimentos_sessao_itens"),
                where("grupoId", "==", usuarioLogado.grupoId),
                where("osId", "==", dados.id)
              )
            );

            const sessaoIds = Array.from(
              new Set(
                snapItensSessao.docs
                  .map((docSnap) => String(docSnap.data()?.sessaoId || ""))
                  .filter(Boolean)
              )
            );

            const sessoes = await Promise.all(
              sessaoIds.map(async (sessaoId) => {
                const sessaoSnap = await getDoc(doc(db, "atendimentos_sessoes", sessaoId));
                if (!sessaoSnap.exists()) return null;
                return sessaoSnap.data();
              })
            );

            const temPropostaAprovada = sessoes.some((sessaoData) => {
              if (!sessaoData) return false;
              return String(sessaoData?.propostaStatus || "").toUpperCase() === "APROVADA"
                || sessaoData?.liberadaAgendamento === true;
            });

            setPropostaAprovada(temPropostaAprovada);
          }
        } catch (eContrato) {
          console.warn("Erro ao validar contrato avulso:", eContrato);
          setOsContratoAvulso(false);
          setPropostaAprovada(false);
        }

        // Verificar se a OS já está finalizada/cancelada
        const statusOsNome = (dados.statusNome || "").toLowerCase().trim();
        const osFinalizada = ["finalizada", "finalizado", "cancelada", "cancelado"].some(
          (s) => statusOsNome.includes(s)
        );

        if (osFinalizada) {
          setEtapa("FINALIZADA");
          setCarregandoOs(false);
          return;
        }

        // Verificar se já existe atendimento em andamento para esta OS
        try {
          if (usuarioLogado?.uid) {
            const atendRef = collection(db, "atendimentos");
            
            // Primeiro: verificar se o usuário atual é o técnico principal
            const qAtPrincipal = query(
              atendRef,
              where("grupoId", "==", usuarioLogado.grupoId),
              where("osId", "==", dados.id),
              where("tecnicoId", "==", usuarioLogado.uid),
              where("statusAtendimento", "==", STATUS_ATENDIMENTO.EM_ANDAMENTO)
            );
            const snapPrincipal = await getDocs(qAtPrincipal);

            if (!snapPrincipal.empty) {
              // Usuário é o técnico principal
              const docAt = snapPrincipal.docs[0];
              const dadosAt = docAt.data();

              setAtendimentoId(docAt.id);
              setTecnicoPrincipalId(dadosAt.tecnicoId);
              setTecnicoPrincipalNome(dadosAt.tecnicoNome);
              setAcompanhantes(dadosAt.acompanhantes || []);
              setModoAcompanhamento(false);
              setEtapa("EM_ANDAMENTO");

              setDefeitoApresentado(dadosAt.defeitoApresentado || descr);
              setDefeitoEncontrado(dadosAt.defeitoEncontrado || "");
              setServicoExecutado(dadosAt.servicoExecutado || "");
              setMaquinaContinuaParada(dadosAt.maquinaContinuaParada || "Não");
              setStatusAtendimento(dadosAt.statusAtendimento || "");

              // Checklist (se houver)
              try {
                const itensChecklist = Array.isArray(dados?.checklistItens) ? dados.checklistItens : [];
                if (dados?.checklistModeloId && itensChecklist.length > 0) {
                  const resp = Array.isArray(dadosAt?.checklistRespostas) ? dadosAt.checklistRespostas : [];
                  const mapped = itensChecklist.map((it, idx) => {
                    const r = resp.find((x) => x?.index === idx || x?.indexOriginal === idx) || resp[idx];
                    return { valor: r?.valor ?? "" };
                  });
                  setChecklistRespostas(mapped);
                  setChecklistObservacoes(dadosAt?.checklistObservacoes || "");
                }
              } catch {
                // ignore
              }

            } else {
              // Verificar se existe atendimento em andamento por OUTRO técnico
              const qAtOutro = query(
                atendRef,
                where("grupoId", "==", usuarioLogado.grupoId),
                where("osId", "==", dados.id),
                where("statusAtendimento", "==", STATUS_ATENDIMENTO.EM_ANDAMENTO)
              );
              const snapOutro = await getDocs(qAtOutro);

              if (!snapOutro.empty) {
                // Existe atendimento por outro técnico
                const docAt = snapOutro.docs[0];
                const dadosAt = docAt.data();

                setAtendimentoId(docAt.id);
                setTecnicoPrincipalId(dadosAt.tecnicoId);
                setTecnicoPrincipalNome(dadosAt.tecnicoNome);
                setAcompanhantes(dadosAt.acompanhantes || []);
                
                // Verificar se o usuário já é acompanhante
                const jaAcompanhante = (dadosAt.acompanhantes || []).some(
                  (a) => a.id === usuarioLogado.uid
                );
                setModoAcompanhamento(jaAcompanhante);
                setEtapa(jaAcompanhante ? "EM_ANDAMENTO" : "PRE");

                setDefeitoApresentado(dadosAt.defeitoApresentado || descr);
                setDefeitoEncontrado(dadosAt.defeitoEncontrado || "");
                setServicoExecutado(dadosAt.servicoExecutado || "");
                setMaquinaContinuaParada(dadosAt.maquinaContinuaParada || "Não");
                setStatusAtendimento(dadosAt.statusAtendimento || "");

              // Checklist (se houver)
              try {
                const itensChecklist = Array.isArray(dados?.checklistItens) ? dados.checklistItens : [];
                if (dados?.checklistModeloId && itensChecklist.length > 0) {
                  const resp = Array.isArray(dadosAt?.checklistRespostas) ? dadosAt.checklistRespostas : [];
                  const mapped = itensChecklist.map((it, idx) => {
                    const r = resp.find((x) => x?.index === idx || x?.indexOriginal === idx) || resp[idx];
                    return { valor: r?.valor ?? "" };
                  });
                  setChecklistRespostas(mapped);
                  setChecklistObservacoes(dadosAt?.checklistObservacoes || "");
                }
              } catch {
                // ignore
              }

              } else {
                setEtapa("PRE");
              }
            }
          } else {
            setEtapa("PRE");
          }
        } catch (eAt) {
          console.error("Erro ao carregar atendimento existente da OS:", eAt);
          setEtapa("PRE");
        }
      } catch (e) {
        console.error("Erro ao carregar OS para atendimento:", e);
        setErro("Erro ao carregar OS. Tente novamente.");
      } finally {
        setCarregandoOs(false);
      }
    }

    carregarOs();
}, [usuarioLogado, osId]);

  useEffect(() => {
    async function carregarTecnicosDisponiveis() {
      if (!usuarioLogado?.grupoId) {
        setTecnicosDisponiveis([]);
        return;
      }

      try {
        const usuariosSnap = await getDocs(
          query(collection(db, "usuarios"), where("grupoId", "==", usuarioLogado.grupoId))
        );

        const lista = usuariosSnap.docs
          .map((userDoc) => ({ id: userDoc.id, ...userDoc.data() }))
          .filter((item) => nivelUsuario(item) >= NIVEIS.TECNICO)
          .sort((a, b) =>
            String(a?.nome || a?.displayName || a?.email || "").localeCompare(
              String(b?.nome || b?.displayName || b?.email || ""),
              "pt-BR",
              { sensitivity: "base" }
            )
          );

        setTecnicosDisponiveis(lista);
      } catch (eTec) {
        console.warn("Erro ao carregar técnicos disponíveis:", eTec);
        setTecnicosDisponiveis([]);
      }
    }

    carregarTecnicosDisponiveis();
  }, [usuarioLogado?.grupoId]);

// Carregar materiais do Estoque Interno (por empresa da OS)
useEffect(() => {
  async function carregarEstoqueInterno() {
    if (!usuarioLogado?.grupoId) return;

    // Preferência: empresa da OS; fallback: empresa do usuário
    const empresaIdBase = os?.empresaId || usuarioLogado?.empresaId || null;

    setCarregandoEstoque(true);
    setMateriaisErro("");
    try {
      const filtros = [where("grupoId", "==", usuarioLogado.grupoId)];
      if (empresaIdBase) filtros.push(where("empresaId", "==", empresaIdBase));

      const ref = collection(db, "estoqueInterno");
      const q = query(ref, ...filtros);
      const snap = await getDocs(q);

      const lista = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(a.produto || a.modelo || "").localeCompare(String(b.produto || b.modelo || ""), "pt-BR", { sensitivity: "base" }));

      setEstoqueItens(lista);
    } catch (e) {
      console.warn("Erro ao carregar estoque interno:", e);
      setEstoqueItens([]);
    } finally {
      setCarregandoEstoque(false);
    }
  }

  carregarEstoqueInterno();
}, [usuarioLogado?.grupoId, usuarioLogado?.empresaId, os?.empresaId]);

const STATUS_BLOQUEADOS = ["finalizada", "finalizado", "cancelada", "cancelado"];
  function isOsFinalizada() {
    const statusNome = (os?.statusNome || "").toLowerCase().trim();
    return STATUS_BLOQUEADOS.some((s) => statusNome.includes(s));
  }

  const aiEmpresaId = os?.empresaId || "";
  const aiChatKey = aiEmpresaId && osId ? `mamute.ai.chat.atendimento.v1:${aiEmpresaId}:${osId}` : "";

  // Carrega config e histórico do chat (persistência local, útil no CWIES)
  useEffect(() => {
    if (!aiEmpresaId) return;

    let alive = true;
    setAiError("");
    apiGetAiConfig(aiEmpresaId)
      .then((cfg) => {
        if (!alive) return;
        setAiCfg(cfg);
      })
      .catch(() => {
        if (!alive) return;
        setAiCfg(null);
      });

    return () => {
      alive = false;
    };
  }, [aiEmpresaId]);

  useEffect(() => {
    if (!aiChatKey) return;
    try {
      const raw = localStorage.getItem(aiChatKey);
      if (!raw) return;
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) setAiChat(arr);
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiChatKey]);

  useEffect(() => {
    if (!aiChatKey) return;
    try {
      localStorage.setItem(aiChatKey, JSON.stringify((aiChat || []).slice(-20)));
    } catch {
      // ignore
    }
  }, [aiChat, aiChatKey]);

  function buildAiSystem() {
    return (
      "Você é o Assistente de Manutenção do Mamute. " +
      "Responda em PT-BR. Seja prático e objetivo. " +
      "Nunca invente dados; se faltar informação, faça perguntas curtas para completar o diagnóstico. " +
      "Quando fizer sentido, entregue: (1) hipóteses prováveis, (2) testes rápidos em ordem, (3) riscos/segurança, (4) próximos passos." 
    );
  }

  function buildAiUserText(userText) {
    const partes = [];
    const cod = os?.codigo || (os?.codigoSequencial != null ? `OS${String(os.codigoSequencial).padStart(4, "0")}` : os?.id || "-");
    partes.push("Contexto da OS (resumo):");
    partes.push(`- Código: ${cod}`);
    partes.push(`- Empresa: ${os?.empresaNome || "-"}`);
    partes.push(`- Setor: ${os?.setorNome || "-"}`);
    partes.push(`- Máquina: ${os?.maquinaNome || "-"}`);
partes.push(`- Defeito/Descrição (abertura): ${(os?.descricao || os?.titulo || "-")}`);
    partes.push(`- Etapa do atendimento: ${etapa}`);
    partes.push("\nCampos preenchidos (até agora):");
    if (defeitoApresentado) partes.push(`- Defeito apresentado: ${defeitoApresentado}`);
    if (defeitoEncontrado) partes.push(`- Defeito encontrado: ${defeitoEncontrado}`);
    if (servicoExecutado) partes.push(`- Serviço executado: ${servicoExecutado}`);
    if (statusAtendimento) partes.push(`- Status atendimento: ${statusAtendimento}`);
    if (maquinaContinuaParada) partes.push(`- Máquina continua parada?: ${maquinaContinuaParada}`);
    if (procedimentosRelacionado?.length) {
      partes.push("\nProcedimentos vinculados:");
      for (const p of procedimentosRelacionado.slice(0, 5)) {
        partes.push(`- ${p?.titulo || p?.nome || p?.id || "(procedimento)"}`);
      }
    }
    if (historicoOs?.length) {
      partes.push("\nHistórico (mesmo defeito):");
      for (const h of historicoOs.slice(0, 3)) {
        const cod = h?.codigo || (h?.codigoSequencial != null ? `OS${String(h.codigoSequencial).padStart(4, "0")}` : h?.id);
        partes.push(`- ${cod} • ${h?.maquinaNome || "-"} • ${h?.statusNome || "-"}`);
      }
    }

    partes.push("\nPergunta do técnico:");
    partes.push(String(userText || "").trim());
    return partes.join("\n");
  }

  async function handleAiSend() {
    setAiError("");
    const pergunta = String(aiInput || "").trim();
    if (!pergunta) return;
    if (!aiEmpresaId) {
      setAiError("Empresa não identificada nesta OS.");
      return;
    }
    if (!aiCfg?.enabled) {
      setAiError("IA desativada para esta empresa (Opções → IA)." );
      return;
    }

    setAiLoading(true);
    try {
      const system = buildAiSystem();
      const userText = buildAiUserText(pergunta);

      const baseHistory = Array.isArray(aiChat) ? aiChat.slice(-14).map((m) => ({ role: m.role, content: m.content })) : [];
      const messages = [...baseHistory, { role: "user", content: userText }];

      const result = await apiChatAi({
        empresaId: aiEmpresaId,
        model: aiCfg?.modelDefault || "gpt-5-mini",
        system,
        messages,
        message: userText,
      });

      const answerText = result?.text || "";
      const now = Date.now();
      setAiChat((prev) => {
        const arr = Array.isArray(prev) ? prev.slice(-18) : [];
        return [...arr, { role: "user", content: pergunta, ts: now - 1 }, { role: "assistant", content: answerText, ts: now }];
      });
      setAiInput("");
    } catch (e) {
      setAiError(String(e?.message || e));
    } finally {
      setAiLoading(false);
    }
  }

  function applyAiToDefeito() {
    const last = [...(aiChat || [])].reverse().find((m) => m.role === "assistant" && m.content);
    if (!last) return;
    setDefeitoEncontrado((prev) => (prev ? `${prev}\n\n${last.content}` : last.content));
  }

  function applyAiToServico() {
    const last = [...(aiChat || [])].reverse().find((m) => m.role === "assistant" && m.content);
    if (!last) return;
    setServicoExecutado((prev) => (prev ? `${prev}\n\n${last.content}` : last.content));
  }

  async function handleIniciarAtendimento() {
    setErro("");
    setSucesso("");

    // ✅ Validar contrato avulso: deve ter proposta aprovada antes de agendar técnico
    if (osContratoAvulso && !propostaAprovada) {
      setErro(
        "Não é possível agendar técnico nesta OS.\n\n" +
        "Este cliente não possui contrato ativo. " +
        "Primeiramente, é necessário enviar uma proposta e aguardar a aprovação antes de agendar o técnico."
      );
      return;
    }

    if (!usuarioLogado?.grupoId) {
      setErro(
        "Não foi possível identificar o grupo do usuário logado. Verifique o cadastro do usuário."
      );
      return;
    }

    if (!os) {
      setErro("OS não carregada. Volte e tente novamente.");
      return;
    }

    if (isOsFinalizada()) {
      setErro("Esta OS já foi finalizada ou cancelada. Não é possível iniciar um novo atendimento.");
      return;
    }

    setSalvando(true);

    try {
      const grupoId = usuarioLogado.grupoId;
      const grupoNome = usuarioLogado.grupoNome || grupoId;

      const tecnicoId = usuarioLogado.uid;
      const tecnicoNome =
        usuarioLogado.displayName ||
        usuarioLogado.nome ||
        usuarioLogado.email ||
        "Técnico";

      const acompanhantesPlanejamento = normalizarListaAcompanhantes(
        await obterAcompanhantesPlanejamentoDaOs()
      );

      const subnumero = await obterProximoSubnumeroAtendimento(os.id, {
        grupoId: usuarioLogado.grupoId,
        empresaId: os.empresaId || usuarioLogado?.empresaId || null,
      });

      const atendimentosRef = collection(db, "atendimentos");

      const docRef = await addDoc(atendimentosRef, {
        grupoId,
        grupoNome,

        empresaId: os.empresaId || usuarioLogado?.empresaId || null,
        empresaNome: os.empresaNome || usuarioLogado?.empresaNome || null,
        setorId: os.setorId || null,
        setorNome: os.setorNome || null,
        maquinaId: os.maquinaId || null,
        maquinaNome: os.maquinaNome || null,
osId: os.id,
        osCodigo: os.codigo || null,
        osCodigoSequencial: os.codigoSequencial ?? null,
        osTitulo: os.titulo || null,
        osDescricao: os.descricao || null,
        osDataAbertura: os.criadoEm || null,

        // Checklist (instalação) — snapshot do checklist do modelo
        checklistModeloId: os.checklistModeloId || null,
        checklistModeloNome: os.checklistModeloNome || null,
        checklistTipo: os.checklistTipo || null,
        checklistItens: Array.isArray(os.checklistItens) ? os.checklistItens : [],
        checklistRespostas: [],
        checklistObservacoes: "",
        checklistExecutadoEm: null,

        subnumero,

        defeitoApresentado:
          (defeitoApresentado || "").trim() ||
          os.descricao ||
          os.titulo ||
          "",
        defeitoEncontrado: "",
        servicoExecutado: "",
        maquinaContinuaParada: maquinaContinuaParada || "Não",
        statusAtendimento: STATUS_ATENDIMENTO.EM_ANDAMENTO,

        tecnicoId,
        tecnicoNome,
        acompanhantes: acompanhantesPlanejamento,

        dataInicio: serverTimestamp(),
        dataFim: null,

        fotos: [],

        criadoEm: serverTimestamp(),
        atualizadoEm: serverTimestamp(),
      });

      setAtendimentoId(docRef.id);
      setTecnicoPrincipalId(tecnicoId);
      setTecnicoPrincipalNome(tecnicoNome);
      setAcompanhantes(acompanhantesPlanejamento);
      setEtapa("EM_ANDAMENTO");

      await sincronizarAcompanhantesNasSessoes(acompanhantesPlanejamento);

      // Atualizar status da OS para "Em Atendimento"
      try {
        const osRef = doc(db, "ordensServico", os.id);
        await updateDoc(osRef, {
          statusId: "fab_em_atendimento",
          statusNome: STATUS_ATENDIMENTO.EM_ANDAMENTO,
          statusCor: CORES_STATUS["em atendimento"],
          tecnicoAtualId: tecnicoId,
          tecnicoAtualNome: tecnicoNome,
          atualizadoEm: serverTimestamp(),
        });
      } catch (eOs) {
        console.error("Erro ao atualizar status da OS:", eOs);
      }

      setSucesso(
        "Atendimento iniciado. Quando finalizar o serviço, clique em Finalizar atendimento."
      );
    } catch (e) {
      console.error("Erro ao iniciar atendimento:", e);
      setErro("Erro ao iniciar atendimento. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  }

  function handleCancelarPreAtendimento() {
    navegarVoltar();
  }

  function irParaFinalizacao() {
    setEtapa("FINALIZACAO");
  }

  // Função para acompanhar o atendimento de outro técnico
  async function handleAcompanharAtendimento() {
    if (!atendimentoId || !usuarioLogado?.uid) return;

    setErro("");
    setSalvando(true);

    try {
      const atendimentoRef = doc(db, "atendimentos", atendimentoId);
      const novoAcompanhante = {
        id: usuarioLogado.uid,
        nome: usuarioLogado.displayName || usuarioLogado.nome || usuarioLogado.email || "Técnico",
        entradaEm: new Date().toISOString(),
      };

      const novaLista = normalizarListaAcompanhantes([...acompanhantes, novoAcompanhante]);
      await updateDoc(atendimentoRef, {
        acompanhantes: novaLista,
        atualizadoEm: serverTimestamp(),
      });

      await sincronizarAcompanhantesNasSessoes(novaLista);

      setAcompanhantes(novaLista);
      setModoAcompanhamento(true);
      setEtapa("EM_ANDAMENTO");
      setSucesso("Você está acompanhando este atendimento.");
    } catch (e) {
      console.error("Erro ao acompanhar atendimento:", e);
      setErro("Erro ao entrar como acompanhante. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  }

  // Função para sair do acompanhamento
  async function handleSairAcompanhamento() {
    if (!atendimentoId || !usuarioLogado?.uid) return;

    setErro("");
    setSalvando(true);

    try {
      const atendimentoRef = doc(db, "atendimentos", atendimentoId);

      const novaLista = normalizarListaAcompanhantes(
        acompanhantes.filter((item) => item.id !== usuarioLogado.uid)
      );

      await updateDoc(atendimentoRef, {
        acompanhantes: novaLista,
        atualizadoEm: serverTimestamp(),
      });

      await sincronizarAcompanhantesNasSessoes(novaLista);

      setAcompanhantes(novaLista);
      setModoAcompanhamento(false);
      setSucesso("Você saiu do acompanhamento.");
      navigate(-1);
    } catch (e) {
      console.error("Erro ao sair do acompanhamento:", e);
      setErro("Erro ao sair do acompanhamento. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  }

  async function handleAdicionarAcompanhante() {
    if (!atendimentoId) {
      setErro("Atendimento não encontrado.");
      return;
    }

    const acompanhanteId = String(novoAcompanhanteId || "").trim();
    if (!acompanhanteId) {
      setErro("Selecione um técnico acompanhante.");
      return;
    }
    if (acompanhanteId === String(tecnicoPrincipalId || "")) {
      setErro("O técnico principal não pode ser acompanhante.");
      return;
    }
    if (acompanhantes.some((item) => String(item?.id || "") === acompanhanteId)) {
      setErro("Este técnico já está como acompanhante.");
      return;
    }

    setErro("");
    setSalvando(true);
    try {
      const tecnico = tecnicosDisponiveis.find((item) => String(item?.id || "") === acompanhanteId);
      const novoAcompanhante = {
        id: acompanhanteId,
        nome: tecnico?.nome || tecnico?.displayName || tecnico?.email || "Técnico",
        entradaEm: new Date().toISOString(),
      };

      const novaLista = normalizarListaAcompanhantes([...acompanhantes, novoAcompanhante]);
      await updateDoc(doc(db, "atendimentos", atendimentoId), {
        acompanhantes: novaLista,
        atualizadoEm: serverTimestamp(),
      });

      await sincronizarAcompanhantesNasSessoes(novaLista);

      setAcompanhantes(novaLista);
      setNovoAcompanhanteId("");
      setSucesso("Acompanhante adicionado.");
    } catch (eAdd) {
      console.error("Erro ao adicionar acompanhante:", eAdd);
      setErro("Erro ao adicionar acompanhante.");
    } finally {
      setSalvando(false);
    }
  }

  async function handleRemoverAcompanhante(acompanhanteId) {
    if (!atendimentoId || !acompanhanteId) return;

    setErro("");
    setSalvando(true);
    try {
      const novaLista = normalizarListaAcompanhantes(
        acompanhantes.filter((item) => String(item?.id || "") !== String(acompanhanteId || ""))
      );

      await updateDoc(doc(db, "atendimentos", atendimentoId), {
        acompanhantes: novaLista,
        atualizadoEm: serverTimestamp(),
      });

      await sincronizarAcompanhantesNasSessoes(novaLista);

      setAcompanhantes(novaLista);
      setSucesso("Acompanhante removido.");
    } catch (eRem) {
      console.error("Erro ao remover acompanhante:", eRem);
      setErro("Erro ao remover acompanhante.");
    } finally {
      setSalvando(false);
    }
  }

function adicionarMaterialEstoque() {
  setMateriaisErro("");

  const eid = String(estoqueSelecionadoId || "").trim();
  const qtd = Number(String(estoqueQtd || "").replace(",", ".")) || 0;

  if (!eid) {
    setMateriaisErro("Selecione um material do estoque.");
    return;
  }
  if (!(qtd > 0)) {
    setMateriaisErro("Informe uma quantidade válida.");
    return;
  }

  const item = estoqueItens.find((x) => x.id === eid);
  if (!item) {
    setMateriaisErro("Material não encontrado no estoque.");
    return;
  }

  setMateriaisUsados((prev) => {
    const idx = prev.findIndex((m) => m.estoqueId === eid);
    if (idx >= 0) {
      const copia = [...prev];
      const atual = copia[idx];
      copia[idx] = { ...atual, quantidade: (Number(atual.quantidade) || 0) + qtd };
      return copia;
    }
    return [
      ...prev,
      {
        estoqueId: eid,
        produto: item.produto || item.modelo || "",
        codigo: item.codigo || "",
        local: item.local || "",
        quantidade: qtd,
      },
    ];
  });

  setEstoqueSelecionadoId("");
  setEstoqueQtd("1");
}

function removerMaterialEstoque(estoqueId) {
  setMateriaisUsados((prev) => prev.filter((m) => m.estoqueId !== estoqueId));
}

async function handleSalvar(e) {

    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuarioLogado?.grupoId) {
      setErro(
        "Não foi possível identificar o grupo do usuário logado. Verifique o cadastro do usuário."
      );
      return;
    }

    if (!os) {
      setErro("OS não carregada. Volte e tente novamente.");
      return;
    }

    if (!atendimentoId) {
      setErro(
        "Nenhum atendimento em andamento encontrado para esta OS e este técnico."
      );
      return;
    }

    const defeitoApresentadoTrim = (defeitoApresentado || "").trim();
    const defeitoEncontradoTrim = (defeitoEncontrado || "").trim();
    const servicoExecutadoTrim = (servicoExecutado || "").trim();

    if (!defeitoApresentadoTrim) {
      setErro("Preencha o campo Defeito Apresentado.");
      return;
    }
    if (!defeitoEncontradoTrim) {
      setErro("Preencha o campo Defeito Encontrado.");
      return;
    }
    if (!servicoExecutadoTrim) {
      setErro("Preencha o campo Serviço Executado.");
      return;
    }

    // Checklist obrigatório para OS de instalação (quando existir checklist)
    try {
      const info = getChecklistInfo(os);
      if (info?.ativo && info?.ehInstalacao) {
        // Se o checklist já foi concluído na tela dedicada, não bloqueia a finalização
        let jaConcluido = !!os?.checklistConcluido || !!os?.checklistUltimaExecucao;
        try {
          const osFresh = await getDoc(doc(db, "ordensServico", os.id));
          if (osFresh.exists()) {
            const d = osFresh.data() || {};
            jaConcluido = !!d.checklistConcluido || !!d.checklistUltimaExecucao;
          }
        } catch {
          // ignore
        }

        if (!jaConcluido) {
          const faltando = (info.itens || []).findIndex((it, idx) => {
            const tipo = normalizarTipoResposta(it?.tipoResposta);
            const v = checklistRespostas?.[idx]?.valor;
            if (tipo === "BAR") return v === null || v === undefined || String(v).trim() === "";
            return String(v || "").trim() === "";
          });
          if (faltando >= 0) {
            setErro(
              `Checklist de instalação pendente (item ${faltando + 1}). Use o botão “Abrir checklist de instalação”.`
            );
            return;
          }
        }
      }
    } catch {
      // ignore
    }

    setSalvando(true);

    try {
      // Upload de fotos da finalização (se houver)
      let fotosUploadadas = [];
      if (fotosFinalizacao.length > 0) {
        setUploadandoFotos(true);
        try {
          const pastaFinalizacao = `ordens_servico_${os.id}_finalizacao`;
          const uploadPromises = fotosFinalizacao.map((file, idx) =>
            uploadToStorage({
              grupoId: usuarioLogado.grupoId,
              empresaId: os.empresaId || null,
              pasta: pastaFinalizacao,
              file,
              filenamePrefix: `finalizacao_${idx + 1}`,
            })
          );
          fotosUploadadas = await Promise.all(uploadPromises);
          setFotosFinalizacaoUrls(fotosUploadadas);
        } catch (uploadErr) {
          console.error("Erro ao enviar fotos da finalização:", uploadErr);
          const errorMsg = uploadErr?.message || "Erro desconhecido";
          if (errorMsg.includes("permission") || errorMsg.includes("unauthorized")) {
            setErro("Erro ao enviar foto da finalização. Você não tem permissão para fazer upload. Verifique com o administrador.");
          } else if (errorMsg.includes("storage")) {
            setErro("Erro ao enviar foto da finalização. Verifique o Firebase Storage e tente novamente.");
          } else if (errorMsg.includes("network") || errorMsg.includes("timeout")) {
            setErro("Erro ao enviar foto da finalização. Verifique sua conexão com a internet e tente novamente.");
          } else {
            setErro(`Erro ao enviar foto da finalização. ${errorMsg}. Tente novamente.`);
          }
          return;
        } finally {
          setUploadandoFotos(false);
        }
      }

const atendimentoRef = doc(db, "atendimentos", atendimentoId);

// Consumo opcional de materiais do Estoque Interno
const materiaisResumo = (materiaisUsados || [])
  .filter((m) => m && String(m.estoqueId || "").trim() !== "" && Number(m.quantidade) > 0)
  .map((m) => ({
    estoqueId: String(m.estoqueId || "").trim(),
    produto: String(m.produto || "").trim(),
    codigo: m.codigo ? String(m.codigo) : "",
    local: m.local ? String(m.local) : "",
    quantidade: Number(m.quantidade) || 0,
  }));

if (materiaisResumo.length > 0) {
  const grupoId = usuarioLogado.grupoId;
  const empresaId = os?.empresaId || null;
  const empresaNome = os?.empresaNome || null;

  const osCodigoLocal =
    os?.codigo ||
    (os?.codigoSequencial != null ? `OS${String(os.codigoSequencial).padStart(4, "0")}` : os?.id || "");

  const tecnicoId = usuarioLogado?.uid || usuarioLogado?.id || null;
  const tecnicoNome =
    usuarioLogado?.displayName || usuarioLogado?.nome || usuarioLogado?.email || "Técnico";

  try {
    await runTransaction(db, async (tx) => {
      for (const mat of materiaisResumo) {
        const estRef = doc(db, "estoqueInterno", mat.estoqueId);
        const estSnap = await tx.get(estRef);

        if (!estSnap.exists()) {
          throw new Error(`Material não encontrado no estoque: ${mat.produto || mat.estoqueId}`);
        }

        const est = estSnap.data() || {};
        const estoqueAtual =
          Number(est.estoque ?? est.quantidadeAtual ?? est.qtd ?? 0) || 0;

        const qtd = Number(mat.quantidade) || 0;
        const novoEstoque = estoqueAtual - qtd;

        if (novoEstoque < 0) {
          throw new Error(
            `Estoque insuficiente para "${mat.produto || est.produto || est.modelo || mat.estoqueId}". Atual: ${estoqueAtual} | Solicitado: ${qtd}`
          );
        }

        // Atualiza estoque (mantém compatibilidade com campos antigos)
        tx.update(estRef, {
          estoque: novoEstoque,
          quantidadeAtual: novoEstoque,
          atualizadoEm: serverTimestamp(),
        });

        // Registra uso do material na OS
        const osMatRef = doc(collection(db, "osMateriais"));
        tx.set(osMatRef, {
          grupoId,
          empresaId,
          empresaNome,

          osId: os?.id || null,
          osCodigo: osCodigoLocal || null,
          atendimentoId: atendimentoId || null,

          tecnicoId,
          tecnicoNome,

          origem: "Estoque Interno",

          estoqueId: mat.estoqueId,
          produto: mat.produto || est.produto || est.modelo || "",
          codigo: mat.codigo || est.codigo || null,
          local: mat.local || est.local || null,

          quantidade: qtd,
          estoqueAntes: estoqueAtual,
          estoqueDepois: novoEstoque,

          criadoEm: serverTimestamp(),
        });
      }
    });
  } catch (e) {
    console.error("Erro ao consumir materiais do estoque:", e);
    setMateriaisErro(e?.message || "Erro ao consumir materiais do estoque.");
    setSalvando(false);
    return;
  }
}

const statusFinal = statusAtendimento?.trim() || STATUS_ATENDIMENTO.FINALIZADO;

      const infoChecklist = getChecklistInfo(os);
      const incluirChecklistNoAtendimento = !!(infoChecklist?.ativo && !infoChecklist?.ehInstalacao);
      const checklistObsTrim = String(checklistObservacoes || "").trim();
      const checklistRespostasPayload = (incluirChecklistNoAtendimento ? (infoChecklist.itens || []).map((it, idx) => ({
        index: idx,
        indexOriginal: it?.indexOriginal ?? idx,
        pergunta: it?.pergunta || "",
        periodicidade: it?.periodicidade || "",
        tipoResposta: it?.tipoResposta || "",
        valor: checklistRespostas?.[idx]?.valor ?? "",
        fotoUrl: it?.fotoUrl || null,
      })) : []);

      await updateDoc(atendimentoRef, {
        defeitoApresentado: defeitoApresentadoTrim,
        defeitoEncontrado: defeitoEncontradoTrim,
        servicoExecutado: servicoExecutadoTrim,
        materiaisUsados: materiaisResumo,
        maquinaContinuaParada: maquinaContinuaParada || "Não",
        statusAtendimento: statusFinal,
        fotosFinalizacao: fotosUploadadas,
        ...(incluirChecklistNoAtendimento
          ? {
              checklistModeloId: os?.checklistModeloId || null,
              checklistModeloNome: os?.checklistModeloNome || null,
              checklistTipo: os?.checklistTipo || null,
              checklistItens: Array.isArray(os?.checklistItens) ? os.checklistItens : [],
              checklistRespostas: checklistRespostasPayload,
              checklistObservacoes: checklistObsTrim,
              checklistExecutadoEm: serverTimestamp(),
            }
          : {}),
        dataFim: serverTimestamp(),
        atualizadoEm: serverTimestamp(),
      });

      // SEMPRE atualizar a OS quando o atendimento for finalizado
      if (os?.id) {
        const normalizar = (txt) =>
          txt
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase();

        const txt = normalizar(statusFinal);

        let novoStatusId = "fab_finalizada";
        let novoStatusNome = "Finalizada";
        let novoStatusCor = "#16a34a";
        let abrirPedidoMaterial = false;

        if (txt.includes("cancel")) {
          novoStatusId = "fab_cancelada";
          novoStatusNome = "Cancelada";
          novoStatusCor = "#dc2626";
        } else if (txt.includes("aguard") && txt.includes("material")) {
          // "Aguardando Material" não deve ser selecionado na finalização; este status é aplicado após salvar o pedido
          novoStatusId = "fab_aguard_mat";
          novoStatusNome = "Aguardando Material";
          novoStatusCor = "#ca8a04";
        } else if (txt.includes("solic") && txt.includes("material")) {
          // Solicitação de material: abre a tela de pedido e depois o pedido coloca a OS em "Aguardando Material"
          novoStatusId = "fab_solic_mat";
          novoStatusNome = "Solicitação de Material";
          novoStatusCor = "#d97706";
          abrirPedidoMaterial = true;
        } else if (
          txt.includes("aguard") &&
          (txt.includes("parada") || txt.includes("parar"))
        ) {
          novoStatusId = "fab_aguard_parada";
          novoStatusNome = "Aguardando Parada de Máquina";
          novoStatusCor = "#64748b";
        }

        const osRef = doc(db, "ordensServico", os.id);

        // ✅ Regra de parada de máquina (fix):
        // Se a OS foi aberta com "Máquina parou = Sim", ela pode continuar aberta,
        // mas o tempo de máquina parada deve PARAR quando no atendimento
        // "Máquina continua parada?" for "Não".
        const osMaquinaParou = String(os?.maquinaParou || "").toLowerCase().trim() === "sim";
        const continuaParada = String(maquinaContinuaParada || "Não").toLowerCase().trim() === "sim";

        const osUpdate = {
          statusId: novoStatusId,
          statusNome: novoStatusNome,
          statusCor: novoStatusCor,
          tecnicoAtualId: null,
          tecnicoAtualNome: null,
          atualizadoEm: serverTimestamp(),
          // Mantém rastreado na OS o último valor informado no atendimento
          maquinaContinuaParadaUltimo: maquinaContinuaParada || "Não",
        };

        // Checklist na OS (instalação ou outros)
        try {
          if (incluirChecklistNoAtendimento) {
            // snapshot da última execução do checklist na própria OS (para histórico/consulta rápida)
            osUpdate.checklistUltimaExecucao = {
              atendimentoId: atendimentoId || null,
              executadoEm: serverTimestamp(),
              modeloId: os?.checklistModeloId || null,
              modeloNome: os?.checklistModeloNome || null,
              tipo: os?.checklistTipo || null,
              respostas: checklistRespostasPayload,
              observacoes: checklistObsTrim || null,
            };
          }
        } catch {
          // ignore
        }

        if (osMaquinaParou) {
          // Se não existir inicio da parada, usar criadoEm (ou agora como fallback)
          if (!os?.maquinaParouEm) {
            osUpdate.maquinaParouEm = os?.criadoEm || serverTimestamp();
          }

          // Parada ativa é o estado atual da parada (não deve depender do status da OS)
          osUpdate.maquinaParadaAtiva = !!continuaParada;

          if (!continuaParada) {
            // Encerrar parada agora
            osUpdate.maquinaVoltouRodarEm = serverTimestamp();
            osUpdate.maquinaParadaEncerradaEm = serverTimestamp();
          } else {
            // Se voltou a ficar parada novamente, zera o fim
            if (os?.maquinaVoltouRodarEm) osUpdate.maquinaVoltouRodarEm = null;
            if (os?.maquinaParadaEncerradaEm) osUpdate.maquinaParadaEncerradaEm = null;
          }
        }

        await updateDoc(osRef, osUpdate);

        await finalizarSessoesSeConcluidas({
          osAtualId: os.id,
          osAtualStatus: osUpdate.statusNome || "",
        });

        if (abrirPedidoMaterial) {
          // Ao final, abrir diretamente a tela de Pedido de Material vinculada à OS
          navigate(`/estoque/pedidos-pecas/os/${os.id}`);
          return;
        }
      }

      setSucesso("Atendimento finalizado com sucesso.");
      // Após finalizar, voltar para a Fila de Atendimento (OS)
      navigate("/ordens-servico");
    } catch (e) {
      console.error("Erro ao salvar atendimento:", e);
      setErro("Erro ao salvar atendimento. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  }

  const tituloPagina = os
    ? `Atendimento - ${os.codigo || os.titulo || os.descricao || os.id}`
    : "Atendimento da OS";

  const codigoOs = os?.codigo || (os?.codigoSequencial != null ? `OS${String(os.codigoSequencial).padStart(4, "0")}` : os?.id || "-");

  const setChecklistValor = (idx, valor) => {
    setChecklistRespostas((prev) => {
      const base = Array.isArray(prev) ? [...prev] : [];
      while (base.length < idx + 1) base.push({ valor: "" });
      base[idx] = { ...(base[idx] || {}), valor };
      return base;
    });
  };

  const renderChecklistSection = (mobile) => {
    const info = getChecklistInfo(os);
    if (!info?.ativo) return null;

    // Para instalação, abrimos o checklist em uma tela dedicada (mesmo fluxo de preventiva)
    if (info.ehInstalacao) {
      const boxStyle = {
        marginTop: 14,
        padding: mobile ? 12 : 14,
        borderRadius: 14,
        background: "rgba(15,23,42,0.35)",
        border: "1px solid rgba(148,163,184,0.18)",
      };

      return (
        <div style={boxStyle}>
          <div style={{ marginBottom: 10 }}>
            <div
              style={{
                fontWeight: 800,
                fontSize: mobile ? "1rem" : "1.02rem",
                color: "var(--cor-primaria)",
                marginBottom: 4,
              }}
            >
              Checklist de Instalação
            </div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>
              Abra o checklist para responder as perguntas.
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              className={mobile ? "m-btn-secondary" : "pc-btn pc-btn-secundario"}
              onClick={() => navigate(`/checklists/executar-os/${os?.id}`)}
            >
              Abrir checklist
            </button>

            {os?.checklistConcluido && (
              <div
                style={{
                  fontSize: 12,
                  opacity: 0.85,
                  alignSelf: "center",
                }}
              >
                Checklist já concluído.
              </div>
            )}
          </div>
        </div>
      );
    }

    const itens = info.itens || [];
    const inputClass = mobile ? "m-input" : "pc-input";
    const textareaClass = mobile ? "m-textarea" : "";

    return (
      <div
        style={{
          marginTop: 14,
          padding: mobile ? 12 : 14,
          borderRadius: 14,
          background: "rgba(15,23,42,0.35)",
          border: "1px solid rgba(148,163,184,0.18)",
        }}
      >
        <div style={{ marginBottom: 10 }}>
          <div
            style={{
              fontWeight: 800,
              fontSize: mobile ? "1rem" : "1.02rem",
              color: "var(--cor-primaria)",
              marginBottom: 4,
            }}
          >
            {info.ehInstalacao ? "Checklist de Instalação" : "Checklist"}
          </div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>
            Preencha os itens abaixo.
          </div>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          {itens.map((item, idx) => {
            const tipo = normalizarTipoResposta(item?.tipoResposta);
            const valor = checklistRespostas?.[idx]?.valor ?? "";

            const opcoes =
              tipo === "SIM_NAO"
                ? ["Sim", "Não"]
                : tipo === "OK_NOK"
                ? ["OK", "NOK"]
                : tipo === "C"
                ? ["Conforme", "Não conforme"]
                : ["OK", "NOK"];

            return (
              <div
                key={idx}
                style={{
                  padding: 12,
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.06)",
                  background: "rgba(0,0,0,0.15)",
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8 }}>
                  {idx + 1}. {item?.pergunta || "-"}
                </div>

                {item?.fotoUrl ? (
                  <div style={{ marginBottom: 10 }}>
                    <img
                      src={item.fotoUrl}
                      alt={item.pergunta || "foto"}
                      style={{
                        width: "100%",
                        maxHeight: mobile ? 220 : 240,
                        objectFit: "cover",
                        borderRadius: 10,
                        border: "1px solid rgba(255,255,255,0.08)",
                      }}
                    />
                  </div>
                ) : null}

                {tipo === "TEXTO" ? (
                  <textarea
                    className={textareaClass}
                    value={valor}
                    onChange={(e) => setChecklistValor(idx, e.target.value)}
                    rows={mobile ? 2 : 3}
                    style={{ width: "100%" }}
                    placeholder="Digite..."
                  />
                ) : tipo === "BAR" ? (
                  <input
                    className={inputClass}
                    type="number"
                    value={valor}
                    onChange={(e) => setChecklistValor(idx, e.target.value)}
                    style={{ width: "100%" }}
                    placeholder="0"
                  />
                ) : (
                  <select
                    className={inputClass}
                    value={valor}
                    onChange={(e) => setChecklistValor(idx, e.target.value)}
                    style={{ width: "100%" }}
                  >
                    <option value="">Selecione...</option>
                    {opcoes.map((op) => (
                      <option key={op} value={op}>
                        {op}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ display: "block", fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
            Observações do checklist (opcional)
          </label>
          <textarea
            className={textareaClass}
            value={checklistObservacoes}
            onChange={(e) => setChecklistObservacoes(e.target.value)}
            rows={mobile ? 2 : 3}
            style={{ width: "100%" }}
            placeholder="Observações..."
          />
        </div>
      </div>
    );
  };

  const renderAiCard = (mobile) => {
    const cardClass = mobile ? "m-card" : "card";
    // Mantém o padrão visual do Mamute (mobile/desktop)
    const btnPrimary = mobile ? "m-btn-primary" : "pc-btn-primario";
    const btnSecondary = mobile ? "m-btn-secondary" : "pc-btn-secundario";

    return (
      <div className={cardClass} style={{ marginBottom: mobile ? 16 : 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
          <div>
            <h3 style={{ fontSize: mobile ? "1rem" : "1.05rem", fontWeight: 700, margin: 0, color: "var(--cor-primaria)" }}>
              Assistente IA (teste)
            </h3>
            <p style={{ fontSize: "0.85rem", color: "var(--cor-texto-apagado)", margin: "4px 0 0 0" }}>
              Ajuda a diagnosticar e sugerir testes — o contexto fica salvo só nesta OS.
            </p>
          </div>
          <button
            type="button"
            className={btnSecondary}
            onClick={() => {
              setAiChat([]);
              setAiError("");
              try {
                if (aiChatKey) localStorage.removeItem(aiChatKey);
              } catch {
                // ignore
              }
            }}
            style={{ whiteSpace: "nowrap" }}
          >
            Limpar
          </button>
        </div>

        {!aiCfg ? (
          <p style={{ color: "var(--cor-texto-apagado)", margin: 0 }}>Carregando configuração da IA...</p>
        ) : !aiCfg.enabled ? (
          <p style={{ color: "var(--cor-texto-apagado)", margin: 0 }}>
            IA desativada para esta empresa. Ative em <strong>Gerenciar → Opções → IA</strong>.
          </p>
        ) : (
          <>
            <div
              style={{
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 10,
                padding: 10,
                maxHeight: mobile ? 220 : 260,
                overflowY: "auto",
                background: "rgba(0,0,0,0.15)",
                marginBottom: 12,
              }}
            >
              {aiChat.length === 0 ? (
                <p style={{ color: "var(--cor-texto-apagado)", margin: 0 }}>
                  Pergunte algo como: <em>"quais testes rápidos faço primeiro?"</em>
                </p>
              ) : (
                aiChat.slice(-16).map((m, idx) => (
                  <div key={idx} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: "0.8rem", color: "var(--cor-texto-apagado)", marginBottom: 4 }}>
                      {m.role === "assistant" ? "IA" : "Você"}
                    </div>
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.35,
                        fontSize: mobile ? "0.95rem" : "0.95rem",
                        padding: 10,
                        borderRadius: 10,
                        background:
                          m.role === "assistant" ? "rgba(59, 130, 246, 0.10)" : "rgba(16, 185, 129, 0.10)",
                        border: "1px solid rgba(255,255,255,0.06)",
                      }}
                    >
                      {m.content}
                    </div>
                  </div>
                ))
              )}
            </div>

            {aiError && (
              <div
                style={{
                  background: "rgba(127, 29, 29, 0.25)",
                  border: "1px solid rgba(220, 38, 38, 0.35)",
                  padding: 10,
                  borderRadius: 10,
                  marginBottom: 12,
                }}
              >
                <p style={{ margin: 0, color: "#fca5a5" }}>{aiError}</p>
              </div>
            )}

            <textarea
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              placeholder="Pergunte para a IA..."
              rows={mobile ? 2 : 3}
              style={{ width: "100%", resize: "vertical" }}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === "Enter") handleAiSend();
              }}
            />

            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                marginTop: 10,
                alignItems: "center",
              }}
            >
              <button
                type="button"
                className={btnPrimary}
                disabled={aiLoading}
                onClick={handleAiSend}
              >
                {aiLoading ? "Consultando..." : "Perguntar"}
              </button>
              <button
                type="button"
                className={btnSecondary}
                disabled={aiChat.length === 0}
                onClick={applyAiToDefeito}
                style={{ whiteSpace: "nowrap" }}
              >
                Aplicar em “Defeito encontrado”
              </button>
              <button
                type="button"
                className={btnSecondary}
                disabled={aiChat.length === 0}
                onClick={applyAiToServico}
                style={{ whiteSpace: "nowrap" }}
              >
                Aplicar em “Serviço executado”
              </button>
              <span style={{ marginLeft: "auto", color: "var(--cor-texto-apagado)", fontSize: "0.8rem" }}>
                Modelo: {aiCfg?.modelDefault || "-"}
              </span>
            </div>
          </>
        )}
      </div>
    );
  };

  // Conteúdo Mobile
  const conteudoMobile = (
    <div style={{ paddingBottom: 100 }}>
      {carregandoOs ? (
        <div className="m-card" style={{ textAlign: "center", padding: 32 }}>
          <p style={{ fontSize: "1rem", color: "var(--cor-texto-apagado)" }}>Carregando OS...</p>
        </div>
      ) : !os ? (
        <div className="m-card" style={{ background: "rgba(127, 29, 29, 0.3)", border: "1px solid rgba(220, 38, 38, 0.5)" }}>
          <p style={{ fontSize: "1rem", color: "#fca5a5" }}>{erro || "OS não encontrada."}</p>
        </div>
      ) : (
        <>
          {erro && (
            <div className="m-card" style={{ background: "rgba(127, 29, 29, 0.3)", border: "1px solid rgba(220, 38, 38, 0.5)", marginBottom: 16 }}>
              <p style={{ fontSize: "1rem", color: "#fca5a5", margin: 0 }}>{erro}</p>
            </div>
          )}

          {sucesso && (
            <div className="m-card" style={{ background: "rgba(2, 44, 34, 0.5)", border: "1px solid rgba(34, 197, 94, 0.5)", marginBottom: 16 }}>
              <p style={{ fontSize: "1rem", color: "#86efac", margin: 0 }}>{sucesso}</p>
            </div>
          )}

          {/* Resumo da OS - Mobile */}
          <div className="m-card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: "1.1rem", fontWeight: 700, marginBottom: 16, color: "var(--cor-primaria)" }}>
              Dados da OS
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <span style={{ fontSize: "0.85rem", color: "var(--cor-texto-apagado)", display: "block" }}>Código</span>
                <span style={{ fontSize: "1.1rem", fontWeight: 600 }}>{codigoOs}</span>
              </div>
              <div>
                <span style={{ fontSize: "0.85rem", color: "var(--cor-texto-apagado)", display: "block" }}>Empresa</span>
                <span style={{ fontSize: "1rem" }}>{os.empresaNome || "-"}</span>
              </div>
              <div>
                <span style={{ fontSize: "0.85rem", color: "var(--cor-texto-apagado)", display: "block" }}>Setor</span>
                <span style={{ fontSize: "1rem" }}>{os.setorNome || "-"}</span>
              </div>
              <div>
                <span style={{ fontSize: "0.85rem", color: "var(--cor-texto-apagado)", display: "block" }}>Máquina</span>
                <span style={{ fontSize: "1rem", fontWeight: 500 }}>{os.maquinaNome || "-"}</span>
              </div>
<div>
                <span style={{ fontSize: "0.85rem", color: "var(--cor-texto-apagado)", display: "block" }}>Defeito / Descrição</span>
                <span style={{ fontSize: "1rem" }}>{os.descricao || os.titulo || "-"}</span>
              </div>
            </div>
          </div>

          {/* Assistente IA - Mobile */}
          {renderAiCard(true)}

          {/* Histórico - Mobile */}
          <div className="m-card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: 12, color: "var(--cor-texto)" }}>
              Histórico com mesmo defeito
            </h3>
            {carregandoHistorico ? (
              <p style={{ fontSize: "0.95rem", color: "var(--cor-texto-apagado)" }}>Carregando...</p>
            ) : historicoOs.length === 0 ? (
              <p style={{ fontSize: "0.95rem", color: "var(--cor-texto-apagado)" }}>Nenhuma OS anterior encontrada.</p>
            ) : (
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {historicoOs.slice(0, 3).map((item) => (
                  <li key={item.id} style={{ fontSize: "0.95rem", marginBottom: 8 }}>
                    <strong>{item.codigo || `OS${String(item.codigoSequencial || 0).padStart(4, "0")}`}</strong> - {item.maquinaNome || "-"}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* OS já finalizada/cancelada - Mobile */}
          {etapa === "FINALIZADA" && (
            <div className="m-card" style={{ textAlign: "center", padding: 32, marginBottom: 16 }}>
              <p style={{ fontSize: "3rem", margin: 0 }}>✅</p>
              <p style={{ fontSize: "1.2rem", fontWeight: 600, color: CORES_STATUS.finalizado, marginTop: 16 }}>
                Esta OS já foi {os?.statusNome?.toLowerCase() || "finalizada"}
              </p>
              <p style={{ fontSize: "1rem", color: "var(--cor-texto-apagado)", marginTop: 8 }}>
                Não é possível iniciar um novo atendimento.
              </p>
              <button
                type="button"
                className="m-btn-secondary"
                onClick={handleCancelarPreAtendimento}
                style={{ marginTop: 24, padding: "14px 32px", fontSize: "1rem" }}
              >
                Voltar
              </button>
            </div>
          )}

          {/* PRE - Outro técnico atendendo (Mobile) */}
          {tecnicoPrincipalId && tecnicoPrincipalId !== usuarioLogado?.uid && etapa === "PRE" && (
            <div className="m-card" style={{ background: "rgba(251, 146, 60, 0.1)", border: "1px solid rgba(251, 146, 60, 0.3)", marginBottom: 16 }}>
              <p style={{ fontSize: "1.05rem", fontWeight: 600, color: "#fb923c", margin: "0 0 8px 0" }}>
                ⚠ OS em atendimento
              </p>
              <p style={{ fontSize: "1rem", margin: 0 }}>
                <strong>Técnico:</strong> {tecnicoPrincipalNome || "Não identificado"}
              </p>
              {acompanhantes.length > 0 && (
                <p style={{ fontSize: "0.95rem", color: "var(--cor-texto-apagado)", marginTop: 8 }}>
                  Acompanhantes: {acompanhantes.map((a) => a.nome).join(", ")}
                </p>
              )}
            </div>
          )}

          {/* PRE sem técnico - Botões (Mobile) */}
          {etapa === "PRE" && !tecnicoPrincipalId && (
            <>
              {osContratoAvulso && !propostaAprovada && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: 12,
                    borderRadius: 8,
                    background: "rgba(251, 146, 60, 0.15)",
                    border: "1px solid rgba(251, 146, 60, 0.3)",
                    color: "#fb923c",
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  ⚠️ <strong>Aguardando proposta aprovada</strong>
                  <p style={{ margin: "4px 0 0 0", fontSize: 12, opacity: 0.9 }}>
                    Este cliente não possui contrato ativo. Envie uma proposta e aguarde aprovação antes de agendar o técnico.
                  </p>
                </div>
              )}
              <div className="m-form-actions">
                <button
                  type="button"
                  className="m-btn-secondary"
                  onClick={handleCancelarPreAtendimento}
                  disabled={salvando}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="m-btn-primary"
                  onClick={handleIniciarAtendimento}
                  disabled={salvando || (osContratoAvulso && !propostaAprovada)}
                >
                  {salvando ? "Iniciando..." : "Iniciar Atendimento"}
                </button>
              </div>
            </>
          )}

          {/* PRE com outro técnico - Botão acompanhar (Mobile) */}
          {etapa === "PRE" && tecnicoPrincipalId && tecnicoPrincipalId !== usuarioLogado?.uid && (
            <div className="m-form-actions">
              <button
                type="button"
                className="m-btn-secondary"
                onClick={handleCancelarPreAtendimento}
                disabled={salvando}
              >
                Voltar
              </button>
              <button
                type="button"
                className="m-btn-primary"
                onClick={handleAcompanharAtendimento}
                disabled={salvando}
              >
                {salvando ? "Entrando..." : "Acompanhar"}
              </button>
            </div>
          )}

          {/* EM ANDAMENTO - Técnico Principal (Mobile) */}
          {etapa === "EM_ANDAMENTO" && !modoAcompanhamento && (
            <>
              <div className="m-card" style={{ marginBottom: 16 }}>
                <p style={{ fontSize: "1rem", fontWeight: 700, margin: "0 0 10px 0", color: "var(--cor-primaria)" }}>
                  👥 Equipe do atendimento
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "end" }}>
                  <div>
                    <label className="m-label" style={{ marginBottom: 6, display: "block" }}>Adicionar acompanhante</label>
                    <select
                      className="m-input"
                      value={novoAcompanhanteId}
                      onChange={(e) => setNovoAcompanhanteId(e.target.value)}
                      disabled={salvando}
                    >
                      <option value="">Selecione...</option>
                      {tecnicosDisponiveis
                        .filter((item) => String(item?.id || "") !== String(tecnicoPrincipalId || ""))
                        .filter((item) => !acompanhantes.some((acc) => String(acc?.id || "") === String(item?.id || "")))
                        .map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.nome || item.displayName || item.email || "Técnico"}
                          </option>
                        ))}
                    </select>
                  </div>
                  <button type="button" className="m-btn-secondary" onClick={handleAdicionarAcompanhante} disabled={salvando}>
                    Adicionar
                  </button>
                </div>

                {acompanhantes.length > 0 && (
                  <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                    {acompanhantes.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          padding: 10,
                          borderRadius: 10,
                          border: "1px solid rgba(148,163,184,0.25)",
                          background: "rgba(15,23,42,0.35)",
                        }}
                      >
                        <span style={{ fontSize: 14 }}>{item.nome || "Técnico"}</span>
                        <button
                          type="button"
                          className="m-btn-secondary"
                          style={{ padding: "8px 10px" }}
                          onClick={() => handleRemoverAcompanhante(item.id)}
                          disabled={salvando}
                        >
                          Remover
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {acompanhantes.length > 0 && (
                <div className="m-card" style={{ background: "rgba(59, 130, 246, 0.1)", border: "1px solid rgba(59, 130, 246, 0.3)", marginBottom: 16 }}>
                  <p style={{ fontSize: "1rem", fontWeight: 600, color: "#60a5fa", margin: 0 }}>
                    👥 Acompanhantes: {acompanhantes.map((a) => a.nome).join(", ")}
                  </p>
                </div>
              )}

              <div style={{ padding: "24px 0" }}>
                <button
                  type="button"
                  className="m-btn-primary"
                  onClick={irParaFinalizacao}
                  disabled={salvando}
                  style={{
                    width: "100%",
                    padding: "18px 24px",
                    fontSize: "1.15rem",
                    fontWeight: 700,
                    borderRadius: 14,
                    background: "linear-gradient(135deg, #16a34a, #15803d)",
                    boxShadow: "0 4px 16px rgba(22, 163, 74, 0.3)",
                  }}
                >
                  Finalizar Atendimento
                </button>
              </div>
            </>
          )}

          {/* EM ANDAMENTO - Modo Acompanhamento (Mobile) */}
          {etapa === "EM_ANDAMENTO" && modoAcompanhamento && (
            <>
              <div className="m-card" style={{ background: "rgba(59, 130, 246, 0.1)", border: "1px solid rgba(59, 130, 246, 0.3)", marginBottom: 16 }}>
                <p style={{ fontSize: "1.05rem", fontWeight: 600, color: "#60a5fa", margin: "0 0 8px 0" }}>
                  👁 Você está acompanhando
                </p>
                <p style={{ fontSize: "1rem", margin: 0 }}>
                  <strong>Responsável:</strong> {tecnicoPrincipalNome || "Não identificado"}
                </p>
                <p style={{ fontSize: "0.9rem", color: "var(--cor-texto-apagado)", marginTop: 12 }}>
                  Somente o técnico responsável pode finalizar.
                </p>
              </div>

              <div style={{ padding: "16px 0" }}>
                <button
                  type="button"
                  className="m-btn-secondary"
                  onClick={handleSairAcompanhamento}
                  disabled={salvando}
                  style={{ width: "100%", padding: "16px 24px", fontSize: "1rem" }}
                >
                  {salvando ? "Saindo..." : "Sair do Acompanhamento"}
                </button>
              </div>
            </>
          )}

          {/* FINALIZACAO - Formulário (Mobile) */}
          {etapa === "FINALIZACAO" && (
            <form onSubmit={handleSalvar}>
              <div className="m-card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: "1.1rem", fontWeight: 700, marginBottom: 20, color: "var(--cor-primaria)" }}>
                  Finalizar Atendimento
                </h3>

                <div className="m-form-group">
                  <label className="m-label">Defeito Apresentado</label>
                  <textarea
                    className="m-textarea"
                    value={defeitoApresentado}
                    onChange={(e) => setDefeitoApresentado(e.target.value)}
                    rows={3}
                    placeholder="Descreva o defeito apresentado..."
                  />
                </div>

                <div className="m-form-group">
                  <label className="m-label">Defeito Encontrado</label>
                  <textarea
                    className="m-textarea"
                    value={defeitoEncontrado}
                    onChange={(e) => setDefeitoEncontrado(e.target.value)}
                    rows={3}
                    placeholder="Descreva o defeito encontrado..."
                  />
                </div>

                <div className="m-form-group">
                  <label className="m-label">Serviço Executado</label>
                  <textarea
                    className="m-textarea"
                    value={servicoExecutado}
                    onChange={(e) => setServicoExecutado(e.target.value)}
                    rows={3}
                    placeholder="Descreva o serviço executado..."
                  />
                </div>

                {renderChecklistSection(true)}

                <div className="m-form-group">
                  <label className="m-label">Máquina continua parada?</label>
                  <select
                    className="m-input"
                    value={maquinaContinuaParada}
                    onChange={(e) => setMaquinaContinuaParada(e.target.value)}
                  >
                    <option value="Não">Não</option>
                    <option value="Sim">Sim</option>
                  </select>
                </div>

                <div className="m-form-group">
                  <label className="m-label">Status do Atendimento</label>
                  <select
                    className="m-input"
                    value={statusAtendimento}
                    onChange={(e) => setStatusAtendimento(e.target.value)}
                  >
                    <option value="">Selecione o status...</option>
                    <option value="Finalizado">Finalizado</option>
                    <option value="Aguardando Aprovação">Aguardando Aprovação</option>
                    <option value="Aguardando Parada de Máquina">Aguardando Parada de Máquina</option>
                    <option value="Solicitação de Material">Solicitação de Material</option>
                    <option value="Em Análise">Em Análise</option>
                    <option value="Pendente">Pendente</option>
                    <option value="Cancelado">Cancelado</option>
                  </select>
                </div>

                <div className="m-form-group">
                  <label className="m-label">Fotos da Finalização (opcional)</label>
                  <input
                    type="file"
                    className="m-input"
                    accept="image/*"
                    multiple
                    capture="environment"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      // Validar tamanho máximo (10MB por foto)
                      const MAX_SIZE = 10 * 1024 * 1024; // 10MB
                      const invalidFiles = files.filter(f => f.size > MAX_SIZE);
                      if (invalidFiles.length > 0) {
                        setErro(`Algumas fotos excedem o tamanho máximo de 10MB. Reduza o tamanho das imagens e tente novamente.`);
                        e.target.value = null;
                        return;
                      }
                      setFotosFinalizacao(files);
                      setErro("");
                    }}
                    disabled={salvando || uploadandoFotos}
                  />
                  {fotosFinalizacao.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "var(--cor-texto-apagado)" }}>
                      {fotosFinalizacao.length} foto(s) selecionada(s)
                    </div>
                  )}
                  {uploadandoFotos && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "var(--cor-primaria)" }}>
                      Enviando fotos...
                    </div>
                  )}
                </div>
              </div>

              {/* Materiais usados (Estoque Interno) */}
              <div className="m-card" style={{ marginBottom: 16 }}>
  <h3 style={{ fontSize: "1.05rem", fontWeight: 700, marginBottom: 12, color: "var(--cor-primaria)" }}>
    Materiais do Estoque Interno (opcional)
  </h3>

  {materiaisErro && (
    <div style={{ background: "#7f1d1d", color: "#fecaca", padding: 8, borderRadius: 10, marginBottom: 10, fontSize: 13 }}>
      {materiaisErro}
    </div>
  )}

  <div className="m-form-group">
    <label className="m-label">Material</label>
    <select
      className="m-input"
      value={estoqueSelecionadoId}
      onChange={(e) => setEstoqueSelecionadoId(e.target.value)}
      disabled={carregandoEstoque || salvando}
    >
      <option value="">
        {carregandoEstoque ? "Carregando estoque..." : "Selecione..."}
      </option>
      {estoqueItens.map((it) => {
        const nome = it.produto || it.modelo || "-";
        const cod = it.codigo ? ` (${it.codigo})` : "";
        const loc = it.local ? ` • ${it.local}` : "";
        const est = Number(it.estoque ?? it.quantidadeAtual ?? 0) || 0;
        return (
          <option key={it.id} value={it.id}>
            {nome}{cod}{loc} — Est: {est}
          </option>
        );
      })}
    </select>
  </div>

  <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
    <div className="m-form-group" style={{ flex: 1, marginBottom: 0 }}>
      <label className="m-label">Quantidade</label>
      <input
        className="m-input"
        type="number"
        min="0"
        step="1"
        value={estoqueQtd}
        onChange={(e) => setEstoqueQtd(e.target.value)}
        disabled={salvando}
      />
    </div>

    <button
      type="button"
      className="m-btn-secondary"
      onClick={adicionarMaterialEstoque}
      disabled={salvando || carregandoEstoque}
      style={{ height: 44, marginBottom: 0 }}
    >
      Adicionar
    </button>
  </div>

  {materiaisUsados.length > 0 && (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>
        Materiais adicionados:
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {materiaisUsados.map((m) => (
          <div
            key={m.estoqueId}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              padding: 10,
              borderRadius: 12,
              border: "1px solid rgba(148,163,184,0.18)",
              background: "rgba(15,23,42,0.35)",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {m.produto || "-"}
              </div>
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                Qtd: <strong>{m.quantidade}</strong>
                {m.local ? ` • ${m.local}` : ""}
              </div>
            </div>

            <button
              type="button"
              className="m-btn-secondary"
              onClick={() => removerMaterialEstoque(m.estoqueId)}
              disabled={salvando}
              style={{ padding: "8px 10px", fontSize: 12, whiteSpace: "nowrap" }}
            >
              Remover
            </button>
          </div>
        ))}
      </div>
    </div>
  )}
</div>

<div className="m-form-actions">
                <button
                  type="button"
                  className="m-btn-secondary"
                  onClick={() => setEtapa("EM_ANDAMENTO")}
                  disabled={salvando}
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  className="m-btn-primary"
                  disabled={salvando}
                >
                  {salvando ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );

  // Mobile Layout
  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo={codigoOs}
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        abaBottom="atend"
      >
        {conteudoMobile}
      </LayoutMobile>
    );
  }

  // Desktop Layout
  return (
    <LayoutDesktop
      tema={tema}
      titulo={tituloPagina}
      subtitulo="Ordens de Serviço / Atendimento"
    >
      <section className="pc-card pc-form-card">
        <header className="pc-form-header">
          <h3>Atendimento da OS</h3>
          <p>
            Primeiro visualize os dados da OS, histórico, procedimentos e
            checklist. Em seguida, inicie o atendimento e, ao final, preencha o
            formulário de finalização.
          </p>
        </header>

        {carregandoOs ? (
          <p style={{ padding: 10 }}>Carregando OS...</p>
        ) : !os ? (
          <div
            style={{
              background: "#7f1d1d",
              color: "#fecaca",
              padding: 8,
              borderRadius: 8,
              marginBottom: 12,
            }}
          >
            {erro || "OS não encontrada."}
          </div>
        ) : (
          <>
            {erro && (
              <div
                style={{
                  background: "#7f1d1d",
                  color: "#fecaca",
                  padding: 8,
                  borderRadius: 8,
                  marginBottom: 12,
                }}
              >
                {erro}
              </div>
            )}

            {sucesso && (
              <div
                style={{
                  background: "#022c22",
                  color: "#a7f3d0",
                  padding: 8,
                  borderRadius: 8,
                  marginBottom: 12,
                }}
              >
                {sucesso}
              </div>
            )}

            {/* Resumo da OS */}
            <div
              style={{
                marginBottom: 16,
                padding: 14,
                borderRadius: 12,
                background: "rgba(15,23,42,0.5)",
                border: "1px solid rgba(148,163,184,0.14)",
                fontSize: 14,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 2 }}>
                    {os.codigo ||
                      (os.codigoSequencial != null
                        ? `OS${String(os.codigoSequencial).padStart(4, "0")}`
                        : os.id)}
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.85 }}>
                    {os.maquinaNome || "-"}
                    
                  </div>
                </div>

                <button
                  type="button"
                  className="pc-btn-secundario"
                  style={{ padding: "6px 10px", fontSize: 12, whiteSpace: "nowrap" }}
                  onClick={() => setOsResumoAberto((v) => !v)}
                >
                  {osResumoAberto ? "Ocultar detalhes" : "Ver detalhes"}
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 10,
                  marginTop: 12,
                }}
              >
                <div>
                  <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 2 }}>EMPRESA</div>
                  <div style={{ fontWeight: 600 }}>{os.empresaNome || "-"}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 2 }}>SETOR</div>
                  <div style={{ fontWeight: 600 }}>{os.setorNome || "-"}</div>
                </div>
                {osResumoAberto && (
                  <>
                    <div>
                      <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 2 }}>MÁQUINA</div>
                      <div style={{ fontWeight: 600 }}>{os.maquinaNome || "-"}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 2 }}>SUBMÁQUINA</div>
</div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 2 }}>DEFEITO / DESCRIÇÃO</div>
                      <div style={{ fontWeight: 600, lineHeight: 1.35 }}>
                        {os.descricao || os.titulo || "-"}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Assistente IA - Desktop */}
            {renderAiCard(false)}

            {/* Histórico e Procedimentos */}
            <div className="pc-form-grid">
              <div className="pc-form-group pc-form-group-full">
                <label>OS anteriores com o mesmo defeito</label>
                {carregandoHistorico ? (
                  <p style={{ fontSize: 13 }}>Carregando histórico...</p>
                ) : historicoOs.length === 0 ? (
                  <p style={{ fontSize: 13, opacity: 0.7 }}>
                    Nenhuma outra OS encontrada com este mesmo defeito.
                  </p>
                ) : (
                  <ul
                    style={{ fontSize: 13, paddingLeft: 16, marginTop: 4 }}
                  >
                    {historicoOs.slice(0, 5).map((item) => (
                      <li key={item.id}>
                        <strong>
                          {item.codigo ||
                            (item.codigoSequencial != null
                              ? `OS${String(
                                  item.codigoSequencial
                                ).padStart(4, "0")}`
                              : item.id)}
                        </strong>{" "}
                        – {item.maquinaNome || "-"} –{" "}
                        {item.descricao || item.titulo || "-"}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="pc-form-group pc-form-group-full">
                <label>Procedimentos vinculados ao equipamento</label>
                {carregandoProcedimentos ? (
                  <p style={{ fontSize: 13 }}>Carregando procedimentos...</p>
                ) : procedimentosRelacionado.length === 0 ? (
                  <p style={{ fontSize: 13, opacity: 0.7 }}>
                    Nenhum procedimento cadastrado para esta máquina.
                  </p>
                ) : (
                  <ul
                    style={{ fontSize: 13, paddingLeft: 16, marginTop: 4 }}
                  >
                    {procedimentosRelacionado.map((p) => (
                      <li key={p.id}>
                        <strong>
                          {p.codigo ||
                            (p.codigoSequencial != null
                              ? `PR${String(
                                  p.codigoSequencial
                                ).padStart(4, "0")}`
                              : p.id)}
                        </strong>{" "}
                        – {p.nome || "-"}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="pc-form-group pc-form-group-full">
                <label>Checklist do equipamento</label>
                <p style={{ fontSize: 13, opacity: 0.7 }}>
                  O checklist específico por tipo de equipamento será detalhado
                  e configurado em uma etapa futura. Por enquanto, utilize os
                  procedimentos como referência para testes de liberação.
                </p>
              </div>
            </div>

            {/* Info de quem está atendendo (se houver) */}
            {tecnicoPrincipalId && tecnicoPrincipalId !== usuarioLogado?.uid && etapa === "PRE" && (
              <div
                style={{
                  marginTop: 16,
                  padding: 16,
                  borderRadius: 12,
                  background: "linear-gradient(135deg, rgba(251, 146, 60, 0.15), rgba(234, 88, 12, 0.1))",
                  border: "1px solid rgba(251, 146, 60, 0.3)",
                }}
              >
                <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: "#fb923c" }}>
                  ⚠ Esta OS já está sendo atendida
                </p>
                <p style={{ margin: "8px 0 0", fontSize: 13, opacity: 0.9 }}>
                  <strong>Técnico responsável:</strong> {tecnicoPrincipalNome || "Não identificado"}
                </p>
                {acompanhantes.length > 0 && (
                  <p style={{ margin: "4px 0 0", fontSize: 13, opacity: 0.8 }}>
                    <strong>Acompanhantes:</strong> {acompanhantes.map((a) => a.nome).join(", ")}
                  </p>
                )}
                <p style={{ margin: "12px 0 0", fontSize: 12, opacity: 0.7 }}>
                  Você pode acompanhar este atendimento para auxiliar o técnico principal.
                </p>
              </div>
            )}

            {/* OS já finalizada/cancelada */}
            {etapa === "FINALIZADA" && (
              <div
                style={{
                  marginTop: 16,
                  padding: 24,
                  borderRadius: 12,
                  background: "rgba(22, 163, 74, 0.1)",
                  border: "2px solid rgba(22, 163, 74, 0.3)",
                  textAlign: "center",
                }}
              >
                <p style={{ margin: 0, fontSize: 48 }}>✅</p>
                <p style={{ margin: "16px 0 0", fontWeight: 600, fontSize: 18, color: CORES_STATUS.finalizado }}>
                  Esta OS já foi {os?.statusNome?.toLowerCase() || "finalizada"}
                </p>
                <p style={{ margin: "12px 0 0", fontSize: 14, opacity: 0.8 }}>
                  Não é possível iniciar um novo atendimento para esta OS.
                </p>
                <button
                  type="button"
                  className="pc-btn-secundario"
                  onClick={handleCancelarPreAtendimento}
                  style={{ marginTop: 20 }}
                >
                  Voltar
                </button>
              </div>
            )}

            {/* Etapas do atendimento - PRE sem técnico */}
            {etapa === "PRE" && !tecnicoPrincipalId && (
              <>
                {osContratoAvulso && !propostaAprovada && (
                  <div
                    style={{
                      marginBottom: 20,
                      padding: 14,
                      borderRadius: 8,
                      background: "rgba(251, 146, 60, 0.12)",
                      border: "1px solid rgba(251, 146, 60, 0.3)",
                      color: "#fb923c",
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  >
                    <div style={{ marginBottom: 6 }}>⚠️ <strong>Aguardando proposta aprovada</strong></div>
                    <p style={{ margin: 0, fontSize: 13, opacity: 0.9 }}>
                      Este cliente não possui contrato ativo. Primeiramente, é necessário enviar uma proposta de serviço e aguardar a aprovação antes de agendar o técnico.
                    </p>
                  </div>
                )}
                <div className="pc-form-actions">
                  <button
                    type="button"
                    className="pc-btn-secundario"
                    onClick={handleCancelarPreAtendimento}
                    disabled={salvando}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="pc-btn-primario"
                    onClick={handleIniciarAtendimento}
                    disabled={salvando || (osContratoAvulso && !propostaAprovada)}
                  >
                    {salvando ? "Iniciando..." : "Iniciar Atendimento"}
                  </button>
                </div>
              </>
            )}

            {/* Opção de acompanhar quando outro técnico está atendendo */}
            {etapa === "PRE" && tecnicoPrincipalId && tecnicoPrincipalId !== usuarioLogado?.uid && (
              <div className="pc-form-actions">
                <button
                  type="button"
                  className="pc-btn-secundario"
                  onClick={handleCancelarPreAtendimento}
                  disabled={salvando}
                >
                  Voltar
                </button>
                <button
                  type="button"
                  className="pc-btn-primario"
                  onClick={handleAcompanharAtendimento}
                  disabled={salvando}
                  style={{ background: "linear-gradient(135deg, #3b82f6, #1d4ed8)" }}
                >
                  {salvando ? "Entrando..." : "Acompanhar Atendimento"}
                </button>
              </div>
            )}

            {/* Em andamento - Técnico Principal */}
            {etapa === "EM_ANDAMENTO" && !modoAcompanhamento && (
              <>
                <div
                  style={{
                    marginTop: 16,
                    padding: 12,
                    borderRadius: 8,
                    background: "rgba(15,23,42,0.35)",
                    border: "1px solid rgba(148,163,184,0.22)",
                    fontSize: 13,
                  }}
                >
                  <p style={{ margin: "0 0 8px 0", fontWeight: 700, color: "var(--cor-primaria)" }}>
                    👥 Equipe do atendimento
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "end" }}>
                    <div>
                      <label style={{ display: "block", fontSize: 12, opacity: 0.75, marginBottom: 6 }}>
                        Adicionar acompanhante
                      </label>
                      <select
                        value={novoAcompanhanteId}
                        onChange={(e) => setNovoAcompanhanteId(e.target.value)}
                        disabled={salvando}
                        style={{ width: "100%" }}
                      >
                        <option value="">Selecione...</option>
                        {tecnicosDisponiveis
                          .filter((item) => String(item?.id || "") !== String(tecnicoPrincipalId || ""))
                          .filter((item) => !acompanhantes.some((acc) => String(acc?.id || "") === String(item?.id || "")))
                          .map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.nome || item.displayName || item.email || "Técnico"}
                            </option>
                          ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      className="pc-btn-secundario"
                      onClick={handleAdicionarAcompanhante}
                      disabled={salvando}
                    >
                      Adicionar
                    </button>
                  </div>

                  {acompanhantes.length > 0 && (
                    <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                      {acompanhantes.map((item) => (
                        <div
                          key={item.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10,
                            padding: 8,
                            borderRadius: 8,
                            border: "1px solid rgba(148,163,184,0.2)",
                            background: "rgba(2,6,23,0.35)",
                          }}
                        >
                          <span>{item.nome || "Técnico"}</span>
                          <button
                            type="button"
                            className="pc-btn-secundario"
                            style={{ padding: "6px 10px", fontSize: 12 }}
                            onClick={() => handleRemoverAcompanhante(item.id)}
                            disabled={salvando}
                          >
                            Remover
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mostrar acompanhantes */}
                {acompanhantes.length > 0 && (
                  <div
                    style={{
                      marginTop: 16,
                      padding: 12,
                      borderRadius: 8,
                      background: "rgba(59, 130, 246, 0.1)",
                      border: "1px solid rgba(59, 130, 246, 0.3)",
                      fontSize: 13,
                    }}
                  >
                    <p style={{ margin: 0, fontWeight: 600, color: "#60a5fa" }}>
                      👥 Técnicos acompanhando: {acompanhantes.map((a) => a.nome).join(", ")}
                    </p>
                  </div>
                )}
                
                <div className="pc-form-actions" style={{ marginTop: 24 }}>
                  <button
                    type="button"
                    className="pc-btn-primario"
                    onClick={irParaFinalizacao}
                    disabled={salvando}
                    style={{
                      padding: "16px 32px",
                      fontSize: "1.1rem",
                      fontWeight: 700,
                    }}
                  >
                    Finalizar Atendimento
                  </button>
                </div>
              </>
            )}

            {/* Em andamento - Modo Acompanhamento */}
            {etapa === "EM_ANDAMENTO" && modoAcompanhamento && (
              <>
                <div
                  style={{
                    marginTop: 16,
                    padding: 16,
                    borderRadius: 12,
                    background: "linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(37, 99, 235, 0.1))",
                    border: "1px solid rgba(59, 130, 246, 0.3)",
                  }}
                >
                  <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: "#60a5fa" }}>
                    👁 Você está acompanhando este atendimento
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: 13, opacity: 0.9 }}>
                    <strong>Técnico responsável:</strong> {tecnicoPrincipalNome || "Não identificado"}
                  </p>
                  {acompanhantes.filter((a) => a.id !== usuarioLogado?.uid).length > 0 && (
                    <p style={{ margin: "4px 0 0", fontSize: 13, opacity: 0.8 }}>
                      <strong>Outros acompanhantes:</strong>{" "}
                      {acompanhantes.filter((a) => a.id !== usuarioLogado?.uid).map((a) => a.nome).join(", ")}
                    </p>
                  )}
                  <p style={{ margin: "12px 0 0", fontSize: 12, opacity: 0.7 }}>
                    Somente o técnico responsável pode finalizar o atendimento.
                  </p>
                </div>

                <div className="pc-form-actions" style={{ marginTop: 24 }}>
                  <button
                    type="button"
                    className="pc-btn-secundario"
                    onClick={handleSairAcompanhamento}
                    disabled={salvando}
                    style={{ padding: "14px 28px" }}
                  >
                    {salvando ? "Saindo..." : "Sair do Acompanhamento"}
                  </button>
                </div>
              </>
            )}

            {etapa === "FINALIZACAO" && (
              <form onSubmit={handleSalvar} className="pc-form">
                <div className="pc-form-group pc-form-group-full">
                  <label>Defeito Apresentado</label>
                  <textarea
                    value={defeitoApresentado}
                    onChange={(e) => setDefeitoApresentado(e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="pc-form-group pc-form-group-full">
                  <label>Defeito Encontrado</label>
                  <textarea
                    value={defeitoEncontrado}
                    onChange={(e) => setDefeitoEncontrado(e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="pc-form-group pc-form-group-full">
                  <label>Serviço Executado</label>
                  <textarea
                    value={servicoExecutado}
                    onChange={(e) => setServicoExecutado(e.target.value)}
                    rows={3}
                  />
                </div>


                {getChecklistInfo(os)?.ativo ? (
                  <div className="pc-form-group pc-form-group-full">
                    {renderChecklistSection(false)}
                  </div>
                ) : null}
                <div className="pc-form-group">
                  <label>Máquina continua parada?</label>
                  <select
                    value={maquinaContinuaParada}
                    onChange={(e) => setMaquinaContinuaParada(e.target.value)}
                  >
                    <option value="Não">Não</option>
                    <option value="Sim">Sim</option>
                  </select>
                </div>

                <div className="pc-form-group">
                  <label>Status do Atendimento</label>
                  <select
                    value={statusAtendimento}
                    onChange={(e) => setStatusAtendimento(e.target.value)}
                  >
                    <option value="">Selecione o status...</option>
                    <option value="Finalizado">Finalizado</option>
                    <option value="Aguardando Aprovação">Aguardando Aprovação</option>
                    <option value="Aguardando Parada de Máquina">Aguardando Parada de Máquina</option>
                    <option value="Solicitação de Material">Solicitação de Material</option>
                    <option value="Em Análise">Em Análise</option>
                    <option value="Pendente">Pendente</option>
                    <option value="Cancelado">Cancelado</option>
                  </select>
                </div>

                <div className="pc-form-group pc-form-group-full">
                  <label>Fotos da Finalização (opcional)</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      // Validar tamanho máximo (10MB por foto)
                      const MAX_SIZE = 10 * 1024 * 1024; // 10MB
                      const invalidFiles = files.filter(f => f.size > MAX_SIZE);
                      if (invalidFiles.length > 0) {
                        setErro(`Algumas fotos excedem o tamanho máximo de 10MB. Reduza o tamanho das imagens e tente novamente.`);
                        e.target.value = null;
                        return;
                      }
                      setFotosFinalizacao(files);
                      setErro("");
                    }}
                    disabled={salvando || uploadandoFotos}
                  />
                  {fotosFinalizacao.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "var(--cor-texto-apagado)" }}>
                      {fotosFinalizacao.length} foto(s) selecionada(s)
                    </div>
                  )}
                  {uploadandoFotos && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "var(--cor-primaria)" }}>
                      Enviando fotos...
                    </div>
                  )}
                </div>

                {/* Materiais usados (Estoque Interno) */}
                <div
                  className="pc-card"
                  style={{
                    marginTop: 12,
                    padding: 14,
                    borderRadius: 14,
                    background: "rgba(15,23,42,0.5)",
                    border: "1px solid rgba(148,163,184,0.14)",
                  }}
                >
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: "var(--cor-primaria)" }}>
      Materiais do Estoque Interno (opcional)
    </h4>
    {carregandoEstoque && (
      <span style={{ fontSize: 12, opacity: 0.75 }}>Carregando estoque...</span>
    )}
  </div>

  {materiaisErro && (
    <div style={{ background: "#7f1d1d", color: "#fecaca", padding: 8, borderRadius: 10, marginTop: 10, fontSize: 13 }}>
      {materiaisErro}
    </div>
  )}

  <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 140px", gap: 10, marginTop: 12, alignItems: "end" }}>
    <div>
      <label style={{ display: "block", fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Material</label>
      <select
        value={estoqueSelecionadoId}
        onChange={(e) => setEstoqueSelecionadoId(e.target.value)}
        disabled={carregandoEstoque || salvando}
        style={{ width: "100%" }}
      >
        <option value="">
          {carregandoEstoque ? "Carregando..." : "Selecione..."}
        </option>
        {estoqueItens.map((it) => {
          const nome = it.produto || it.modelo || "-";
          const cod = it.codigo ? ` (${it.codigo})` : "";
          const loc = it.local ? ` • ${it.local}` : "";
          const est = Number(it.estoque ?? it.quantidadeAtual ?? 0) || 0;
          return (
            <option key={it.id} value={it.id}>
              {nome}{cod}{loc} — Est: {est}
            </option>
          );
        })}
      </select>
    </div>

    <div>
      <label style={{ display: "block", fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Quantidade</label>
      <input
        type="number"
        min="0"
        step="1"
        value={estoqueQtd}
        onChange={(e) => setEstoqueQtd(e.target.value)}
        disabled={salvando}
        style={{ width: "100%" }}
      />
    </div>

    <button
      type="button"
      className="pc-btn-secundario"
      onClick={adicionarMaterialEstoque}
      disabled={salvando || carregandoEstoque}
      style={{ height: 40, whiteSpace: "nowrap" }}
    >
      Adicionar
    </button>
  </div>

  {materiaisUsados.length > 0 && (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 8 }}>Materiais adicionados:</div>

      <div style={{ display: "grid", gap: 8 }}>
        {materiaisUsados.map((m) => (
          <div
            key={m.estoqueId}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              padding: 10,
              borderRadius: 12,
              border: "1px solid rgba(148,163,184,0.18)",
              background: "rgba(15,23,42,0.35)",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {m.produto || "-"}
              </div>
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                Qtd: <strong>{m.quantidade}</strong>
                {m.local ? ` • ${m.local}` : ""}
              </div>
            </div>

            <button
              type="button"
              className="pc-btn-secundario"
              onClick={() => removerMaterialEstoque(m.estoqueId)}
              disabled={salvando}
              style={{ padding: "8px 10px", fontSize: 12, whiteSpace: "nowrap" }}
            >
              Remover
            </button>
          </div>
        ))}
      </div>
    </div>
  )}
</div>

<div className="pc-form-actions">
                  <button
                    type="button"
                    className="pc-btn-secundario"
                    onClick={() => setEtapa("EM_ANDAMENTO")}
                    disabled={salvando}
                  >
                    Voltar
                  </button>
                  <button
                    type="submit"
                    className="pc-btn-primario"
                    disabled={salvando}
                  >
                    {salvando ? "Salvando..." : "Salvar Atendimento"}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </section>
    </LayoutDesktop>
  );
}

export default AtendimentoForm;
